import {
    animationFrameScheduler,
    from,
    fromEvent,
    fromEventPattern,
    merge, Observable,
    of,
    scheduled,
    Subject,
    Subscription
} from 'rxjs';
import {auditTime, debounceTime, filter, finalize, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';

export type CompareFn = (cellA: CellNode, cellB: CellNode, rowA: RowNode, rowB: RowNode, def: Sort) => number;
export type CanBeDroppedOnFn = (drag: CellNode, drop: CellNode) => boolean;

export interface Column<T = any> {
    id: string;
    name?: string;
    minWidth?: number;
    setWidth?: number;
    width?: number;
    height?: number;
    color?: string;
    children?: Array<Column<T>>;
    compareFn?: CompareFn;
    canBeDroppedOnFn?: CanBeDroppedOnFn;
    sticky?: 'left' | 'right';
    rowSpan?: number;
    cellSpan?: number;
    hidden?: boolean;
    ctx?: T;

    willMeasure?: (node: CellNode) => void;
    didMeasure?: (node: CellNode) => void;
    measureElement?: (node: CellNode) => Element;
}


////////////////////


export type InnerElementFn = <T extends BaseNode>(parentNode: T, node: T) => HTMLElement | Promise<HTMLElement>;
export type DetectChangesFn = (action: 'append' | 'insert' | 'layout' | 'force') => void;
export type DestroyFn = <T extends BaseNode>(node: T) => void;

export interface RenderContext {
    element: HTMLElement;
    innerElement?: InnerElementFn;
    detectChanges: DetectChangesFn;
    destroy: DestroyFn;
}

export type RenderFn = (context: Context) => RenderContext;

export interface Definition<T = any> {
    ctx?: T;
    render?: RenderFn;
}

export interface Grid<T = any> extends Definition<T> {
}

export interface Row<TRowContext = any, TCellContext = any> extends Definition<TRowContext> {
    id: string;
    height?: number;
    hidden?: boolean;
    cells: Array<Cell<TCellContext>>;
    children?: Array<Row<TRowContext, TCellContext>>;
}

export interface Cell<T = any> extends Definition<T> {
    columnId: string;
    height?: number;
    willMeasure?: (node: CellNode) => void;
    didMeasure?: (node: CellNode) => void;
    measureElement?: (node: CellNode) => Element;
}


////////////////////


export enum SortOrder {
    ASC,
    DESC
}

export interface Sort {
    columnId: string;
    order: SortOrder;
}

export function inverseSortOrder(column: Column, opts: Sort, defaultOrder: SortOrder): Sort {
    if (!column) return null;
    const same = opts && opts.columnId === column.id;
    return {
        columnId: column.id,
        order: same ? (opts.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC) : defaultOrder
    };
}

export function sortOrderWithDefault(columns: Array<Column>, column: Column, current: Sort, defaultSort: Sort): Array<Sort> {
    if (!column) {
        column = current && columns.find(e => e.id === current.columnId);
    } else {
        current = inverseSortOrder(column, current, SortOrder.ASC);
    }
    const sortDefs: Array<Sort> = [{...current}];
    if (column.id !== defaultSort.columnId) {
        sortDefs.push({columnId: defaultSort.columnId, order: SortOrder.ASC});
    }
    return sortDefs;
}


////////////////////


export interface Context<TColumnContext = any, TRowContext = any, TContext = any> {
    t: 'grid' | 'row' | 'cell';
    c?: Column<TColumnContext>;
    g?: GridNode;
    r?: RowNode<TRowContext>;
    l?: CellNode;
    ctx?: TContext;
}

export interface BaseNode {
    def: Definition;
    context: Context;
    renderContext: RenderContext;

    detectChanges(opts?: BubbleOpts): void;
}

function append<TNode extends BaseNode, TParentNode extends BaseNode, TDefinition extends Definition>(
    renderer: Renderer,
    constructNodeFn: (V) => TNode,
    collection: Array<TNode>,
    parentNode: TParentNode,
    defs: Array<TDefinition>
): void {
    if (!renderer || !collection) return;
    defs.forEach(d => {
        const n = constructNodeFn(d);
        collection.push(n);
        if (n instanceof RowNode) {
            const dd = d as any as Row;
            n.cells.setChildren(dd.cells, {emitEvent: false});
        }
        if (n instanceof NodeWithChildren) {
            const dd = d as any as Row;
            if (dd.children) {
                n.setChildren(dd.children, {emitEvent: false});
            }
        }
        if (parentNode instanceof GridNode) {
            renderer.appendChild(parentNode, n);
        }
        if (parentNode instanceof RowNode) {
            const nextEl = parentNode.children.length <= 1 /*current node is already pushed above*/ ? parentNode : parentNode.children[parentNode.children.length - 2];
            // renderer.gridNode.element.insertBefore(n.element, nextEl);
            // renderer.insertBeforeNode(renderer.gridNode, n, nextEl);
            renderer.insertAfter(renderer.gridNode, n, nextEl);
        }
        if (parentNode instanceof NodeWithCells) {
            renderer.appendChild(parentNode, n);
        }
        // n.detectChanges();
    });
}

function insert<TNode extends BaseNode, TParentNode extends BaseNode, TDefinition extends Definition>(
    renderer: Renderer,
    constructNodeFn: (V) => TNode,
    collection: Array<TNode>,
    parentNode: TParentNode,
    defs: Array<TDefinition>,
    index: number
): void {
    if (!renderer || !collection) return;
    defs.forEach((d, i) => {
        const n = constructNodeFn(d);
        collection.splice(index + i, 0, n);
        if (n instanceof RowNode) {
            const dd = d as any as Row;
            n.cells.setChildren(dd.cells, {emitEvent: false});
        }
        if (n instanceof NodeWithChildren) {
            const dd = d as any as Row;
            if (dd.children) {
                n.setChildren(dd.children, {emitEvent: false});
            }
        }
        // if (parentNode instanceof GridNode) {
        //     if (index + i + 1 === collection.length) {
        //         renderer.appendChild(parentNode, n);
        //     } else {
        //         renderer.insertBefore(parentNode, n, collection[index + i + 1]);
        //     }
        // }
        // if (parentNode instanceof RowNode) {
        //     if (index + i + 1 === collection.length) {
        //         // parentNode.element.insertBefore(n.element, collection[collection.length - 1].renderContext.element.nextSibling);
        //         // renderer.insertBeforeNode(parentNode, n, collection[collection.length - 1].renderContext.element.nextSibling);
        //         renderer.insertAfter(parentNode, n, collection[collection.length - 1]);
        //     } else {
        //         renderer.insertBefore(parentNode, n, collection[index + i + 1]);
        //     }
        // }
        if (parentNode instanceof GridNode || parentNode instanceof RowNode) {
            if (index === 0) {
                renderer.insertAfter(renderer.gridNode, n, parentNode);
            } else {
                renderer.insertAfter(renderer.gridNode, n, collection[index - 1]);
            }
        }
        if (parentNode instanceof NodeWithCells) {
            if (index === 0) {
                renderer.appendChild(parentNode, n);
            } else {
                renderer.insertAfter(parentNode, n, collection[index - 1]);
            }
        }
        // n.detectChanges();
    });
}

function remove<TNode extends BaseNode, TParentNode extends BaseNode>(
    renderer: Renderer,
    collection: Array<TNode>,
    parentNode: TParentNode,
    start?: number,
    removeCount?: number
) {
    if (!renderer || !collection) return;
    const _start = start || 0;
    const _removeCount = removeCount || collection.length - _start;
    collection.splice(_start, _removeCount).forEach(n => {
        if (n instanceof NodeWithChildren) {
            n.removeChildren(undefined, undefined, {emitEvent: false});
        }
        if (n instanceof RowNode) {
            n.cells.removeChildren(undefined, undefined, {emitEvent: false});
        }
        if (parentNode instanceof GridNode || parentNode instanceof RowNode) {
            renderer.removeChild(renderer.gridNode, n);
        }
        if (parentNode instanceof NodeWithCells) {
            renderer.removeChild(parentNode, n);
        }
        // renderer.destroy(n);
    });
}

function replace<TNode extends BaseNode, TParentNode extends BaseNode, TDefinition extends Definition>(
    renderer: Renderer,
    constructNodeFn: (V) => TNode,
    collection: Array<TNode>,
    parentNode: TParentNode,
    def: TDefinition,
    index: number
): void {
    if (index !== -1) {
        remove(renderer, collection, parentNode, index, 1);
        insert(renderer, constructNodeFn, collection, parentNode, [def], index);
    } else {
        append(renderer, constructNodeFn, collection, parentNode, [def]);
    }
    // let n = collection[index];
    // collection.splice(index, 1);
    // if (n instanceof RowNode) {
    //     n.cells.removeChildren();
    // }
    // if (parentNode instanceof GridNode || parentNode instanceof RowNode) {
    //     renderer.removeChild(renderer.gridNode, n);
    // }
    // if (parentNode instanceof NodeWithCells) {
    //     renderer.removeChild(parentNode, n);
    // }
    // renderer.destroy(n);
    // n = constructNodeFn(def);
    // collection.splice(index, 0, n);
    // if (n instanceof RowNode) {
    //     n.cells.setChildren(def.cells);
    // }
    // if (parentNode instanceof GridNode || parentNode instanceof RowNode) {
    //     if (index === 0) {
    //         renderer.insertAfter(renderer.gridNode, n, parentNode);
    //     } else {
    //         renderer.insertAfter(renderer.gridNode, n, collection[index - 1]);
    //     }
    // }
    // if (parentNode instanceof NodeWithCells) {
    //     if (index === 0) {
    //         renderer.appendChild(parentNode, n);
    //     } else {
    //         renderer.insertAfter(parentNode, n, collection[index - 1]);
    //     }
    // }
    // n.detectChanges();
}

function numberCompareFn(cellA: CellNode, cellB: CellNode, rowA: RowNode, rowB: RowNode, def: Sort): number {
    const retA = cellA.context.ctx == null ? Number.MIN_SAFE_INTEGER : cellA.context.ctx;
    const retB = cellB.context.ctx == null ? Number.MIN_SAFE_INTEGER : cellB.context.ctx;
    return def.order === SortOrder.ASC
        ? retA - retB
        : retB - retA;
}

function stringCompareFn(cellA: CellNode, cellB: CellNode, rowA: RowNode, rowB: RowNode, def: Sort): number {
    const retA = cellA.context.ctx == null ? '' : cellA.context.ctx;
    const retB = cellB.context.ctx == null ? '' : cellB.context.ctx;
    return def.order === SortOrder.ASC
        ? retA.localeCompare(retB)
        : retB.localeCompare(retA);
}

function rowCompareFn<T extends RowNode>(renderer: Renderer, a: T, b: T, def: Sort): number {
    const column = findColumn(renderer.opts.columns, def.columnId);
    const cellA = a.cells.find(l => l.def.columnId === def.columnId);
    const cellB = b.cells.find(l => l.def.columnId === def.columnId);
    return column.compareFn
        ? column.compareFn(cellA, cellB, a, b, def)
        : (typeof cellA.context.ctx === 'number'
            ? numberCompareFn(cellA, cellB, a, b, def)
            : stringCompareFn(cellA, cellB, a, b, def));
}

function cellCompareFn<T extends CellNode>(renderer: Renderer, a: T, b: T, def: Sort): number {
    const retA = findColumnIndex(renderer.opts.columns, a.def.columnId);
    const retB = findColumnIndex(renderer.opts.columns, b.def.columnId);
    return retA - retB;
}

type sortCompareFn<T> = (renderer: Renderer, a: T, b: T, def: Sort) => number;
type sortChildFn<T> = (child: T) => void;
type sortFilterFn<T> = (child: T) => boolean;

function sort<TNode extends BaseNode, TParentNode extends BaseNode>(
    renderer: Renderer,
    collection: Array<TNode>,
    parentNode: TParentNode,
    defs: Array<Sort>,
    compareFn: sortCompareFn<TNode>,
    childFn?: sortChildFn<TNode>,
    filterFn?: sortFilterFn<TNode>
): void {
    if (!renderer || !collection || !defs || defs.length < 1) return;
    const sorted = filterFn
        ? collection.filter(child => filterFn(child))
        : collection.slice();
    const unsorted = collection.map((child, index) => ({index, child}))
        .filter(indexChild => sorted.indexOf(indexChild.child) === -1);
    sorted.sort((a, b) => {
        let ret = 0;
        for (let i = 0; i < defs.length; i++) {
            const def = defs[i];
            ret = compareFn(renderer, a, b, def);
            if (ret !== 0) break;
        }
        return ret;
    });
    const sortedUnsorted = collection.slice()
        .sort((a, b) => sorted.indexOf(a) - sorted.indexOf(b));
    unsorted.forEach(indexChild => {
        const idx = sortedUnsorted.indexOf(indexChild.child);
        sortedUnsorted.splice(idx, 1);
        sortedUnsorted.splice(indexChild.index, 0, indexChild.child);
    });
    collection.sort((a, b) => sortedUnsorted.indexOf(a) - sortedUnsorted.indexOf(b));
    sorted.forEach(n => {
        if (parentNode instanceof GridNode || parentNode instanceof NodeWithCells) {
            renderer.appendChild(parentNode, n);
        }
        if (parentNode instanceof RowNode) {
            // renderer.gridNode.element.insertBefore(n.element, parentNode.element.nextSibling);
            // renderer.insertBeforeNode(renderer.gridNode, n, parentNode.element.nextSibling);
            renderer.insertAfter(renderer.gridNode, n, parentNode);
        }
    });
    if (childFn) {
        collection.forEach(child => childFn(child));
    }
}


////////////////////


export class CellNode<T extends BaseNode = RowNode> implements BaseNode {
    def: Cell;
    context: Context;
    renderContext: RenderContext;
    parent: T;

    constructor(protected _renderer: Renderer) {
    }

    detectChanges(opts?: BubbleOpts): void {
        try {
            this.renderContext.detectChanges('force');
        } catch {
        }
    }
}

abstract class NodeArray<T> {
    abstract nodes(): Array<T>;

    indexOf(node: T): number {
        return this.nodes().indexOf(node);
    }

    get length(): number {
        return this.nodes().length;
    }

    at(index: number): T {
        return this.nodes()[index];
    }

    find(fn: (node?: T, index?: number) => boolean): T {
        for (const n in this.nodes()) {
            const node = this.nodes()[n];
            if (node instanceof NodeArray) {
                const found = node.find(fn);
                if (found) return found;
            } else {
                if (fn(node, 0)) return node;
            }
        }
        return undefined;
    }

    filter(fn: (node?: T, index?: number) => boolean): Array<T> {
        let filtered = [];
        this.nodes().forEach(node => {
            if (node instanceof NodeArray) {
                filtered = filtered.concat(node.filter(fn));
            } else {
                if (fn(node, 0)) {
                    filtered = filtered.concat(node);
                }
            }
        });
        return filtered;
    }

    reduce<ZZ>(fn: (pNode?: ZZ, cNode?: T, index?: number) => ZZ, initValue: ZZ): ZZ {
        let reduced = initValue;
        this.nodes().forEach((node, nodeIndex) => {
            if (node instanceof NodeArray) {
                reduced = node.reduce(fn, reduced);
            } else {
                reduced = fn(reduced, node, nodeIndex);
            }
        });
        return reduced;
    }

    forEach(fn: (node?: T, index?: number, array?: Array<T>) => void): void {
        this.nodes().forEach((node, nodeIndex, nodeArray) => {
            if (node instanceof NodeArray) {
                node.forEach(fn);
            } else {
                fn(node, nodeIndex, nodeArray);
            }
        });
    }
}

interface ChildrenChangeEvent {
    which: 'set' | 'append' | 'insert' | 'remove' | 'update' | 'sort';
}

interface CellDragEvent {
    which: 'dragstart' | 'dragover' | 'drop' | 'dragenter' | 'dragleave' | 'dragend';
    drag: CellNode;
    drop: CellNode;
    columnsDest?: Array<Column>; // drop only
    columnsSrc?: Array<Column>; // drop only
}

interface BubbleOpts {
    emitEvent?: boolean;
    onlySelf?: boolean;
}

abstract class NodeWithChildren<T extends BaseNode, U extends BaseNode, V extends Definition> extends NodeArray<T> implements BaseNode {
    abstract def: Definition;
    abstract context: Context;
    abstract renderContext: RenderContext;

    parent: U;
    children: Array<T>;
    childrenChange: Subject<ChildrenChangeEvent>;
    drag: Subject<CellDragEvent>; // TODO revise. only for columns

    abstract renderNodeFn(): (def: V) => T;

    sortCompareFn?(defs: Array<Sort>, filterFn?: sortFilterFn<T>, opts?: BubbleOpts): sortCompareFn<T>;

    sortChildFn?(defs: Array<Sort>, filterFn?: sortFilterFn<T>, opts?: BubbleOpts): sortChildFn<T>;

    public constructor(protected _renderer: Renderer, protected _parentIsTypeofChild?: boolean) {
        super();
        this.children = [];
        this.childrenChange = new Subject();
        this.drag = new Subject();
    }

    detectChanges(opts?: BubbleOpts): void {
        if (opts && opts.onlySelf) return;
        this.children.forEach(n => n.detectChanges());
    }

    nodes(): Array<T> {
        return this.children;
    }

    find(fn: (node?: T, index?: number) => boolean): T {
        if (this._parentIsTypeofChild) {
            if (fn(this as any as T)) return this as any as T;
        }
        return super.find(fn);
    }

    filter(fn: (node?: T, index?: number) => boolean): Array<T> {
        let filtered = [];
        if (this._parentIsTypeofChild) {
            if (fn(this as any as T)) filtered.push(this);
        }
        filtered = filtered.concat(super.filter(fn));
        return filtered;
    }

    reduce<ZZ>(fn: (pNode?: ZZ, cNode?: T, index?: number) => ZZ, initValue: ZZ): ZZ {
        let reduced = initValue;
        if (this._parentIsTypeofChild) {
            reduced = fn(reduced, this as any as T, 0);
        }
        return super.reduce(fn, reduced);
    }

    forEach(fn: (node: T, index?: number, array?: Array<T>) => void): void {
        if (this._parentIsTypeofChild) {
            fn(this as any as T, 0, [this as any as T]);
        }
        super.forEach(fn);
    }

    setChildren(defs: Array<V>, opts?: BubbleOpts): void {
        this.removeChildren(undefined, undefined, {emitEvent: false});
        this.appendChildren(defs, {emitEvent: false});
        if (!opts || opts.emitEvent) {
            this.childrenChange.next({which: 'set'});
        }
    }

    appendChildren(defs: Array<V>, opts?: BubbleOpts): void {
        append(this._renderer, this.renderNodeFn(), this.children, this as any as U, defs);
        if (!opts || opts.emitEvent) {
            this.childrenChange.next({which: 'append'});
        }
    }

    insertChildren(defs: Array<V>, index: number, opts?: BubbleOpts): void {
        insert(this._renderer, this.renderNodeFn(), this.children, this as any as U, defs, index);
        if (!opts || opts.emitEvent) {
            this.childrenChange.next({which: 'insert'});
        }
    }

    removeChildren(start?: number, removeCount?: number, opts?: BubbleOpts): void {
        remove(this._renderer, this.children, this as any as U, start, removeCount);
        if (!opts || opts.emitEvent) {
            this.childrenChange.next({which: 'remove'});
        }
    }

    setChild(def: V, index: number, opts?: BubbleOpts): void {
        replace(this._renderer, this.renderNodeFn(), this.children, this as any as U, def, index);
        if (!opts || opts.emitEvent) {
            this.childrenChange.next({which: 'set'});
        }
    }

    sort(defs: Array<Sort>, filterFn?: sortFilterFn<T>, opts?: BubbleOpts): void {
        sort(this._renderer, this.children, this as any as U, defs, this.sortCompareFn(defs, filterFn, opts), !this.sortChildFn ? null : this.sortChildFn(defs, filterFn, opts), filterFn);
        if (!opts || opts.emitEvent) {
            this.childrenChange.next({which: 'sort'});
        }
    }
}

class NodeWithCells extends NodeWithChildren<CellNode, RowNode, Cell> {
    get def(): Row {
        return this.parent.def;
    }

    get context(): Context {
        return this.parent.context;
    }

    get renderContext(): RenderContext {
        return this.parent.renderContext;
    }

    renderNodeFn(): (def: Cell) => CellNode {
        return def => this._renderer.cell(def, this.parent);
    }

    sortCompareFn(defs: Array<Sort>, filterFn?: sortFilterFn<CellNode>, opts?: BubbleOpts): sortCompareFn<CellNode> {
        return (renderer: Renderer, a: CellNode, b: CellNode, def: Sort) => cellCompareFn(this._renderer, a, b, def);
    }

    setChildByColumnId(def: Cell, columnId: string): void {
        const index = this.indexOf(this.find(n => n.context.c.id === columnId));
        super.setChild(def, index);
    }
}

export class RowNode<T = any> extends NodeWithChildren<RowNode, GridNode | RowNode, Row> implements BaseNode {
    def: Row<T>;
    context: Context<any, any, T>;
    renderContext: RenderContext;
    cells: NodeWithCells;
    sticky: string;

    constructor(protected _renderer: Renderer) {
        super(_renderer, true);
        this.cells = new NodeWithCells(this._renderer);
        this.cells.parent = this;
    }

    detectChanges(opts?: BubbleOpts): void {
        if (!opts || !opts.onlySelf) {
            super.detectChanges();
        }
        this.cells.detectChanges();
        try {
            this.renderContext.detectChanges('force');
        } catch {
        }
    }

    renderNodeFn(): (def: Row) => RowNode {
        return def => this._renderer.row(def, this);
    }

    sortCompareFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>, opts?: BubbleOpts): sortCompareFn<RowNode> {
        return (renderer: Renderer, a: RowNode, b: RowNode, def: Sort) => rowCompareFn(this._renderer, a, b, def);
    }

    sortChildFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>, opts?: BubbleOpts): sortChildFn<RowNode> {
        return row => row.sort(defs, filterFn, opts);
    }
}

class NodeWithRows extends NodeWithChildren<RowNode, GridNode, Row> {
    get def(): Grid {
        return this.parent.def;
    }

    get context(): Context {
        return this.parent.context;
    }

    get renderContext(): RenderContext {
        return this.parent.renderContext;
    }

    renderNodeFn(): (def: Row) => RowNode {
        return def => this._renderer.row(def, this.parent);
    }

    sortCompareFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>, opts?: BubbleOpts): sortCompareFn<RowNode> {
        return (renderer: Renderer, a: RowNode, b: RowNode, def: Sort) => rowCompareFn(this._renderer, a, b, def);
    }
}

export class GridNode extends NodeWithChildren<RowNode, GridNode, Row> implements BaseNode {
    def: Grid;
    context: Context;
    renderContext: RenderContext;
    rowsTop: NodeWithRows;
    rowsBottom: NodeWithRows;

    constructor(protected _renderer: Renderer) {
        super(_renderer);

        this.rowsTop = new NodeWithRows(this._renderer);
        this.rowsTop.parent = this;

        this.rowsBottom = new NodeWithRows(this._renderer);
        this.rowsBottom.parent = this;
    }

    detectChanges(opts?: BubbleOpts): void {
        if (!opts || !opts.onlySelf) {
            super.detectChanges();
        }
        try {
            this.renderContext.detectChanges('force');
        } catch {
        }
    }

    renderNodeFn(): (def: Row) => RowNode {
        return def => this._renderer.row(def, this);
    }

    sortCompareFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>, opts?: BubbleOpts): sortCompareFn<RowNode> {
        return (renderer: Renderer, a: RowNode, b: RowNode, def: Sort) => rowCompareFn(this._renderer, a, b, def);
    }

    sortChildFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>, opts?: BubbleOpts): sortChildFn<RowNode> {
        return row => row.sort(defs, filterFn, opts);
    }

    updateChildren(defs: Array<Row>): void {
        const rows = flattenObj<GridNode, RowNode>(this, 'children');
        update(defs, rows, this);
        this.childrenChange.next({which: 'update'});
        // this._renderer.layout();
    }
}

function update<T extends RowNode, U extends GridNode | RowNode, V extends Row>(defs: Array<V>, lookup: Array<T>, node: T | U/*, changed?: Array<T | U>*/): void {
    // const changed_ = changed || [];
    defs.forEach(d => {
        const ni = lookup.findIndex(n => n.def.id === d.id);
        if (ni === -1) {
            node.appendChildren([d], {emitEvent: false});
            // changed_.push(node);
        } else {
            const n = lookup[ni];
            if (d === n.def) {
                if (d.children) {
                    update(d.children, lookup, n/*, changed_*/);
                }
            } else {
                n.parent.setChild(d, n.parent.indexOf(n), {emitEvent: false});
                // changed_.push(node);
            }
        }
    });
}

function renderNode(node: BaseNode, renderFn: RenderFn): void {
    // node.renderContext = renderFn(node.context);
    noZone(() => node.renderContext = renderFn(node.context));
}

function destroyNode(node: BaseNode): void {
    if (node.renderContext) {
        node.renderContext.element = null;
        node.renderContext.innerElement = null;
        node.renderContext.detectChanges = null;
        node.renderContext.destroy(node);
        node.renderContext.destroy = null;
    }
    node.renderContext = null;
}

function getElement(parentNode: BaseNode, node: BaseNode): HTMLElement | Promise<HTMLElement> {
    return parentNode.renderContext.innerElement && typeof parentNode.renderContext.innerElement === 'function'
        ? parentNode.renderContext.innerElement(parentNode, node)
        : parentNode.renderContext.element;
}

function getElement2(parentNode: BaseNode, node: BaseNode): Promise<HTMLElement> {
    const ret = getElement(parentNode, node);
    return ret instanceof Promise ? ret : Promise.resolve(ret);
}

export interface RendererOptions {
    useStandardAutofit?: boolean;
    columns: Array<Column>;
    defaultRenderFn: RenderFn;
}

export class Renderer {
    gridNode: GridNode;

    constructor(public readonly opts?: RendererOptions) {
    }

    grid(def: Grid): GridNode {
        const node = new GridNode(this);
        node.def = def;
        node.context = {t: 'grid', g: node, ctx: def.ctx};
        return node;
    }

    row(def: Row, parentNode: GridNode | RowNode): RowNode {
        const node = new RowNode(this);
        node.def = def;
        node.parent = parentNode;
        node.context = {t: 'row', g: this.gridNode, r: node, ctx: def.ctx};
        return node;
    }

    cell(def: Cell, parentNode: RowNode): CellNode {
        const node = new CellNode(this);
        node.def = def;
        node.parent = parentNode;
        node.context = {
            t: 'cell',
            g: this.gridNode,
            c: findColumn(this.opts.columns, def.columnId),
            r: parentNode,
            l: node,
            ctx: def.ctx
        };
        return node;
    }

    render(node: BaseNode): void {
        // const renderFn = node.context.t === 'grid'
        //     ? (node.def.render || this.opts.defaultRenderFn)
        //     : node.context.t === 'row'
        //         ? (node.def.render || this.opts.defaultRenderFn)
        //         : node.context.t === 'cell'
        //             ? (node.def.render || this.opts.defaultRenderFn)
        //             : null;
        renderNode(node, node.def.render || this.opts.defaultRenderFn);
    }

    destroy(node: BaseNode): void {
        destroyNode(node);
        // if (node instanceof GridNode || node instanceof RowNode) {
        //     node.removeChildren();
        // }
    }

    appendChild(parentNode: BaseNode, node: BaseNode): void | Promise<HTMLElement> {
        if (!parentNode.renderContext || !node.renderContext) return;
        // getElement(parentNode, node).appendChild(node.renderContext.element);
        const ret = getElement(parentNode, node);
        if (ret instanceof Promise) {
            return ret.then(element => {
                if (!element) {
                    console.log('promise');
                    return;
                }
                element.appendChild(node.renderContext.element);
                return element;
            });
        } else {
            ret.appendChild(node.renderContext.element);
        }
    }

    insertBefore(parentNode: BaseNode, node: BaseNode, siblingNode: BaseNode): void | Promise<HTMLElement> {
        if (!parentNode.renderContext || !node.renderContext) return;
        // getElement(parentNode, node).insertBefore(node.renderContext.element, siblingNode.renderContext.element);
        const ret = getElement(parentNode, node);
        if (ret instanceof Promise) {
            return ret.then(element => {
                element.insertBefore(node.renderContext.element, siblingNode.renderContext.element);
                return element;
            });
        } else {
            ret.insertBefore(node.renderContext.element, siblingNode.renderContext.element);
        }
    }

    insertAfter(parentNode: BaseNode, node: BaseNode, siblingNode: BaseNode): void | Promise<HTMLElement> {
        if (!parentNode.renderContext || !node.renderContext) return;
        // getElement(parentNode, node).insertBefore(node.renderContext.element, siblingNode.renderContext.element.nextSibling);
        const ret = getElement(parentNode, node);
        if (ret instanceof Promise) {
            return ret.then(element => {
                element.insertBefore(node.renderContext.element, siblingNode.renderContext.element.nextSibling);
                return element;
            });
        } else {
            ret.insertBefore(node.renderContext.element, siblingNode.renderContext.element.nextSibling);
        }
    }

    removeChild(parentNode: BaseNode, node: BaseNode): void {
        if (!node.renderContext || !node.renderContext.element.parentElement) return;
        // getElement(parentNode, node).removeChild(node.renderContext.element);
        node.renderContext.element.parentElement.removeChild(node.renderContext.element);
    }

    layout(): void {
        //
    }
}

export class Controller {
    static usingDef<TRenderer extends Renderer, TRenderOptions extends RendererOptions>(
        rendererType: new (opts) => TRenderer, def: Grid, opts?: TRenderOptions
    ): TRenderer { // will return a controller in the future
        const renderer = new rendererType(opts);
        renderer.gridNode = renderer.grid(def);
        renderer.render(renderer.gridNode);
        return renderer;
    }
}

declare const Zone;

function noZone(fn: Function): void {
    if (window['Zone'] && Zone.root) {
        Zone.root.run(fn);
    } else {
        fn();
    }
}

// TODO move
export function flattenObj<T, U = T>(obj: T, key: string): Array<U> {
    const flat = [];
    if (!obj.hasOwnProperty(key)) return flat;
    if (Array.isArray(obj[key])) {
        obj[key].forEach(e => {
            flat.push(e);
            flat.push(...flattenObj(e, key));
        });
    } else {
        flat.push(obj[key]);
        flat.push(...flattenObj(obj[key], key));
    }
    return flat;
}

// TODO move
export function flattenArrayWithObj<T, U = T>(arr: Array<T>, key: string): Array<U> {
    return arr.reduce((p, c) => {
        p.push(c);
        if (c.hasOwnProperty(key) && c[key]) {
            const result = Array.isArray(c[key]) ? flattenArrayWithObj(c[key], key) : flattenObj(c[key], key);
            p.push(...result);
        }
        return p;
    }, []);
}

function getScrollBarWidth() {
    const fill = document.createElement('div');
    fill.style.width = '100px';
    fill.style.height = '100px';

    const vp = document.createElement('div');
    vp.style.position = 'absolute';
    vp.style.top = '0';
    vp.style.left = '0';
    vp.style.width = '100px';
    vp.style.height = '100px';
    vp.style.overflow = 'hidden';
    vp.style.visibility = 'hidden';
    vp.appendChild(fill);

    document.body.appendChild(vp);
    const w1 = fill.offsetWidth;
    vp.style.overflow = 'scroll';
    const w2 = w1 !== fill.offsetWidth ? fill.offsetWidth : vp.clientWidth;
    document.body.removeChild(vp);

    return w1 - w2;
}

interface ContentAdjacentDiv {
    content: HTMLElement;
    viewport: HTMLElement;
}

interface ContentDiv {
    left: ContentAdjacentDiv;
    right: ContentAdjacentDiv;
    content: HTMLElement;
    viewport: HTMLElement;
    root: HTMLElement;
    boundBox: HTMLElement;
}

function createRootViewport(): HTMLElement {
    const viewport = document.createElement('div');
    viewport.setAttribute('viewport', '');
    viewport.style.display = 'flex';
    viewport.style.position = 'relative';
    viewport.style.zIndex = '0';
    return viewport;
}

function createContentAdjacentDiv(): ContentAdjacentDiv {
    const content = document.createElement('div');
    content.setAttribute('content', '');
    content.style.height = '100%';
    content.style.overflow = 'hidden';

    const viewport = document.createElement('div');
    viewport.setAttribute('viewport', '');
    viewport.style.height = '100%';
    viewport.style.overflow = 'hidden';
    viewport.appendChild(content);

    return {content, viewport};
}

function createContentDiv(): ContentDiv {
    const content = document.createElement('div');
    content.setAttribute('content', '');
    content.style.height = '100%';
    // content.style.overflowX = 'auto';
    // content.style.overflowY = 'hidden';
    content.style.overflow = 'hidden';

    const viewport = document.createElement('div');
    viewport.setAttribute('viewport', '');
    viewport.style.overflowX = 'auto';
    viewport.style.overflowY = 'hidden';
    viewport.style.width = '100%';
    viewport.appendChild(content);

    const root = document.createElement('div');
    root.setAttribute('root', '');
    root.style.display = 'flex';
    root.style.overflow = 'hidden';
    root.style.flexGrow = '1';
    root.appendChild(viewport);

    const left = createContentAdjacentDiv();
    left.content.setAttribute('left', '');
    left.viewport.setAttribute('left', '');

    const right = createContentAdjacentDiv();
    right.content.setAttribute('right', '');
    right.viewport.setAttribute('right', '');

    const boundBox = document.createElement('div');
    boundBox.setAttribute('bound-box', '');
    boundBox.style.position = 'absolute';
    boundBox.style.top = '0';
    boundBox.style.right = '0';
    boundBox.style.bottom = '0';
    boundBox.style.left = '0';
    boundBox.style.pointerEvents = 'none';
    boundBox.style.zIndex = '1';

    return {
        left,
        right,
        content,
        viewport,
        root,
        boundBox
    };
}

const getrow = (def, k, renderer: DivRenderer, orig?: GridNode | RowNode): RowNode => {
    let r = renderer.rowcache[k].find(n => n.def.id === def.id);
    if (r) return r;
    if (!orig) return;
    r = renderer.row(def, orig.parent && getrow(orig.parent.def, k, renderer));
    r.sticky = k;
    renderer.rowcache[k].push(r);
    return r;
};

function setCellAttributes(n: CellNode, index: number, lastIndex: number, visibleIndexStart: number, visibleIndexEnd: number): void {
    const element = n.renderContext.element;

    element.setAttribute('column-id', n.def.columnId);
    element.setAttribute('index', index.toString());

    element.removeAttribute('first');
    index === 0 && element.setAttribute('first', '');
    element.removeAttribute('last');
    index === lastIndex && element.setAttribute('last', '');

    element.removeAttribute('first-visible');
    index === visibleIndexStart && element.setAttribute('first-visible', '');
    element.removeAttribute('last-visible');
    index === visibleIndexEnd && element.setAttribute('last-visible', '');
}

function setRowAttributes(n: RowNode, index: number, lastIndex: number, visibleIndexStart: number, visibleIndexEnd: number): void {
    const element = n.renderContext.element;

    element.setAttribute('id', n.def.id);
    element.setAttribute('parent-id', n.parent instanceof RowNode ? n.parent.def.id : '');
    element.setAttribute('index', index.toString());

    element.removeAttribute('first');
    index === 0 && element.setAttribute('first', '');
    element.removeAttribute('last');
    index === lastIndex && element.setAttribute('last', '');

    element.removeAttribute('first-visible');
    index === visibleIndexStart && element.setAttribute('first-visible', '');
    element.removeAttribute('last-visible');
    index === visibleIndexEnd && element.setAttribute('last-visible', '');
}

function positionNode(
    n: BaseNode,
    content: HTMLElement,
    i: number,
    current: Array<any>,
): void {
    const domIndex = current.indexOf(n);
    if (domIndex === i) return;
    if (i >= current.length) {
        content.appendChild(n.renderContext.element);
    } else {
        content.insertBefore(n.renderContext.element, current[i].renderContext.element);
    }

    if (i >= current.length) {
        if (domIndex !== -1) {
            current.splice(domIndex, 1);
        }
        current.push(n);
    } else {
        let offset = 0;
        if (domIndex !== -1) {
            offset = domIndex > i ? 0 : -1;
            current.splice(domIndex, 1);
        }
        current.splice(i + offset, 0, n);
    }
}

function layoutCellNodeAdjacent(
    n: CellNode,
    content: HTMLElement,
    renderer: DivRenderer,
    i: number,
    current: Array<any>,
    index: number,
    lastIndex: number,
    visibleIndexStart: number,
    visibleIndexEnd: number
): Promise<any> {
    if (!n.renderContext) {
        renderer.render(n);
    }
    const hook = () => {
        n.renderContext.element.style.display = n.context.c.hidden ? 'none' : 'inline-flex';

        setCellAttributes(n, index, lastIndex, visibleIndexStart, visibleIndexEnd);

        positionNode(n, content, i, current);
    };
    return getElement2(n, null).then(() => hook());
}

function layoutRowNodeAdjacent(
    parent: RowNode,
    n: RowNode,
    content: ContentDiv | ContentAdjacentDiv,
    renderer: DivRenderer,
    rr: Renderer,
    i: number,
    current: Array<any>,
    index: number,
    lastIndex: number,
    visibleIndexStart: number,
    visibleIndexEnd: number,
    which: 'left' | 'right'
): Promise<any> {
    if (!n.renderContext) {
        renderer.render(n);
    }
    const hook = () => {
        n.renderContext.element.style.display = n.def.hidden ? 'none' : 'block';

        const moveback = n.cells.filter(l => !hasStickyColumn(l.context.c, which));
        moveback.forEach(l => parent.cells.renderContext.element.appendChild(l.renderContext.element));
        n.cells.children = parent.cells.filter(l => hasStickyColumn(l.context.c, which));
        // TODO reassign cell parent and run through runCellScroll
        const visibleindexstart = parent.cells.indexOf(n.cells.children[0]);
        const visibleindexend = parent.cells.indexOf(n.cells.children[n.cells.length - 1]);
        n.cells.forEach((l, li) => {
            getElement2(n, null)
                .then(element => {
                    const wasNotRendered = !l.renderContext;
                    layoutCellNodeAdjacent(l, element, renderer, li, [], n.cells.indexOf(l), n.cells.length - 1, visibleindexstart, visibleindexend)
                        .then(() => {
                            if (wasNotRendered) {
                                l.detectChanges({onlySelf: true});
                            }
                        });
                });
        });

        setRowAttributes(n, index, lastIndex, visibleIndexStart, visibleIndexEnd);

        positionNode(n, content.content, i, current);

        return moveback.length;
    };
    return getElement2(n, null).then(() => hook());
}

function layoutCellNode(
    n: CellNode,
    content: HTMLElement,
    renderer: DivRenderer,
    i: number,
    cells: Array<CellNode>,
    current: Array<any>,
    index: number,
    lastIndex: number,
    visibleIndexStart: number,
    visibleIndexEnd: number
): void {
    if (!n.renderContext) {
        renderer.render(n);
    }

    n.renderContext.element.style.display = n.context.c.hidden ? 'none' : 'inline-flex';

    setCellAttributes(n, index, lastIndex, visibleIndexStart, visibleIndexEnd);

    positionNode(n, content, i, current);
}

function layoutRowNode(
    n: RowNode,
    content: ContentDiv | ContentAdjacentDiv,
    renderer: DivRenderer,
    rr: Renderer,
    i: number,
    current: Array<any>,
    index: number,
    lastIndex: number,
    visibleIndexStart: number,
    visibleIndexEnd: number
): void {
    if (!n.renderContext) {
        renderer.render(n);
    }

    n.renderContext.element.style.display = n.def.hidden ? 'none' : 'block';

    // if (n.sticky == null) {
    runCellScroll(renderer, n);
    // }

    setRowAttributes(n, index, lastIndex, visibleIndexStart, visibleIndexEnd);

    positionNode(n, content.content, i, current);
}

function columnRows(node: GridNode, renderer: DivRenderer, columns: Array<Column>) {
    const rows = new NodeWithRows(renderer);
    rows.parent = node;
    let defs: Array<Row> = [];
    let level = 0;
    let children = columns;
    while (children.length !== 0) {
        defs.push({
            id: `column${level}`,
            cells: children.map(c => ({
                ctx: c.ctx || c.name,
                columnId: c.id,
                height: c.height,
                willMeasure: c.willMeasure,
                didMeasure: c.didMeasure,
                measureElement: c.measureElement
            })),
            children: []
        });
        level++;
        children = children.filter(c => c.children && c.children.length !== 0).reduce((p, c) => p.concat(c.children), []);
    }
    rows.setChildren(defs);
    return rows;
}

function findColumn(columns: Array<Column>, id: string): Column {
    let found;
    for (const i in columns) {
        const c = columns[i];
        if (c.id === id) {
            found = c;
            break;
        }
        if (c.children) {
            found = findColumn(c.children, id);
            if (found) break;
        }
    }
    return found;
}

function findColumnIndex(columns: Array<Column>, id: string): number {
    let found;
    for (const i in columns) {
        const c = columns[i];
        if (c.id === id) {
            found = i;
            break;
        }
        if (c.children) {
            found += findColumnIndex(c.children, id);
            if (found) break;
        }
    }
    return found;
}

function calcColumnWidth(column: Column, excludeHidden?: boolean): number {
    if (!column || (excludeHidden && column.hidden)) return 0;
    if (column.width != null && !column.hidden && column.width === 0) return 100; // TODO revise
    if (column.width != null && column.minWidth != null) return Math.max(column.width, column.minWidth)
    if (column.width != null) return column.width;
    if (!column.children || column.children.length === 0) return 100;
    return column.children.reduce((p, c) => p + calcColumnWidth(c, excludeHidden), 0);
}

function calcCellWidth(columnId: string, columns: Array<Column>, columnsLeft: Array<Column>, columnsRight: Array<Column>, sticky?: 'left' | 'right', excludeHidden?: boolean): number {
    return calcColumnWidth(
        sticky === 'left'
            ? findColumn(columnsLeft, columnId)
            : sticky === 'right'
            ? findColumn(columnsRight, columnId)
            : findColumn(columns, columnId), excludeHidden);
}

function findTallestCell(row: RowNode): number {
    return row.cells.reduce((p, c) => c.def.height != null ? c.def.height : p, 0);
}

function calcRowHeight(row: RowNode): number {
    if (!row || row.def.hidden) return 0;
    return findTallestCell(row) || (row.def.height != null ? row.def.height : 25);
}

function sumContentWidth(cells: Array<CellNode> | NodeWithCells): number {
    return (cells as Array<CellNode>).reduce((p, c) => p + calcColumnWidth(c.context.c, true), 0);
}

function sumContentHeight(rows: Array<RowNode> | NodeWithRows): number {
    return (rows as Array<RowNode>).reduce((p, c) => p + calcRowHeight(c), 0);
}

function avgContentCellWidth(cells: Array<CellNode> | NodeWithCells): number {
    return sumContentWidth(cells) / cells.length;
}

function avgContentRowHeight(rows: Array<RowNode> | NodeWithRows): number {
    return sumContentHeight(rows) / rows.length;
}

function numCellsVisibleInWidth(cells: Array<CellNode> | NodeWithCells, width: number): { num: number; width: number; } {
    let w = 0;
    let n = 0;
    while ((w < width) && (n < cells.length)) {
        const l: CellNode = cells[n];
        w += calcColumnWidth(l.context.c, true);
        n++;
    }
    return {num: n, width: w};
}

function numRowsVisibleInHeight(rows: Array<RowNode> | NodeWithRows, height: number): { num: number; height: number; } {
    let h = 0;
    let n = 0;
    while ((h < height) && (n < rows.length)) {
        h += calcRowHeight(rows[n]);
        n++;
    }
    return {num: n, height: h};
}

function hasStickyColumn(column: Column, which: 'left' | 'right'): boolean {
    if (!column) return false;
    return (which == null && column.sticky == null) ||
        (column.sticky === which || (column.children && column.children.some(c => hasStickyColumn(c, which))));
}

function reconstructColumns(columns: Array<Column>, which: 'left' | 'right'): Array<Column> {
    return columns.map(c => ({...c, children: !c.children ? null : [...c.children]}))
        // return columns.map(c => {
        //     c.children = !c.children ? null : [...c.children];
        //     return c;
        // })
        .filter(c => {
            if (c.children) {
                c.children = reconstructColumns(c.children, which);
            }
            return hasStickyColumn(c, which);
        });
}

// function moveCell(moving: CellNode, before: CellNode, fromIndex: number, toIndex: number, row: RowNode): void {
//     row.cells.children.splice(fromIndex, 1);
//     row.cells.children.splice(toIndex, 0, moving);
//     if (!before.renderContext || !moving.renderContext) return;
//     if (before === row.cells.children[row.cells.children.length - 1]) {
//         before.renderContext.element.parentElement.appendChild(moving.renderContext.element);
//     } else {
//         if (fromIndex > toIndex) {
//             before.renderContext.element.parentElement.insertBefore(moving.renderContext.element, before.renderContext.element);
//         } else {
//             before.renderContext.element.parentElement.insertBefore(moving.renderContext.element, before.renderContext.element.nextSibling);
//         }
//     }
// }
//
// function moveCellNode(node: RowNode, movingId: string, beforeId: string): void {
//     const moving = node.cells.find(l => l.context.c.id === movingId);
//     const before = node.cells.find(l => l.context.c.id === beforeId);
//     if (!moving || !before) return;
//     const fromIndex = node.cells.indexOf(moving);
//     const toIndex = node.cells.indexOf(before);
//     if (fromIndex === -1 || toIndex === -1) return;
//     moveCell(moving, before, fromIndex, toIndex, node);
// }

function syncCellsToColumns(renderer: DivRenderer): void {
    const flat = flattenArrayWithObj(renderer.columnsInternal.columns, 'children');
    const moveCells = n => {
        sort(renderer, n.cells.children, n.cells, [null], (renderer2: Renderer, a: CellNode, b: CellNode, def: Sort) => {
            const aa = flat.findIndex(c => c.id === a.context.c.id);
            const bb = flat.findIndex(c => c.id === b.context.c.id);
            return aa - bb;
        }, null, l => !l.context.c.sticky);
    };

    renderer.contentColumns.columns.forEach(moveCells);
    renderer.gridNode.forEach(moveCells);
    renderer.gridNode.rowsTop.forEach(moveCells);
    renderer.gridNode.rowsBottom.forEach(moveCells);
}

function moveColumnByIndex(dest: Array<Column>, src: Array<Column>, destIndex: number, srcIndex: number): any {
    // const movingCol = src.splice(srcIndex, 1)[0];
    dest.splice(destIndex, 0, src[srcIndex]);
    src.splice(srcIndex + (dest === src && destIndex < srcIndex ? 1 : 0), 1);
    return {
        dest,
        src,
        destIndex: destIndex + (dest === src && destIndex > srcIndex ? -1 : 0),
        srcIndex: srcIndex + (dest === src && destIndex < srcIndex ? 1 : 0)
    };
}

function moveColumn(dest: Array<Column>, src: Array<Column>, movingId: string, adjacentId: string, pos: 'before' | 'after' = 'before'): any {
    const movingCol = src.find(c => c.id === movingId);
    const adjacentCol = dest.find(c => c.id === adjacentId);
    const fromIndex = src.indexOf(movingCol);
    const toIndex = dest.indexOf(adjacentCol) + (pos === 'before' ? 0 : 1);
    return {...moveColumnByIndex(dest, src, toIndex, fromIndex), pos};
}

interface DragState {
    dragging?: boolean;
    columns?: Array<Column>;
    parent?: Column;
    node?: CellNode;
    dragDropHdl?: HTMLElement;
}

function bindDragDrop(node: CellNode, renderer: DivRenderer): void {
    const clearDropIndicator = (dHdl: HTMLElement) => {
        if (!dHdl) return;
        dHdl.style.backgroundColor = '';
    };
    const showDropIndicator = (node2: CellNode, x: number): HTMLElement => {
        const box = node2.renderContext.element.getBoundingClientRect();
        const qu = x >= box.left + box.width / 2 ? node2.renderContext.element : node2.renderContext.element.previousElementSibling;
        if (!qu) return;
        const dHdl = qu.querySelector('[drag-drop]') as HTMLElement;
        if (dHdl) {
            dHdl.style.backgroundColor = '#000';
        }
        return dHdl;
    };
    const el = node.renderContext.element;
    el.draggable = true;
    merge(fromEvent(el, 'dragstart'),
        fromEvent(el, 'dragend'),
        fromEvent(el, 'dragenter'),
        fromEvent(el, 'dragleave'),
        fromEvent(el, 'dragover'),
        fromEvent(el, 'drop'))
        .pipe(
            tap((e: DragEvent) => {
                const target = (e.currentTarget || e.target) as HTMLElement;
                const state = renderer.dragState;

                // TODO allow movement anywhere
                const nodes = renderer.contentColumns.columns.reduce<Array<CellNode>>((p, c) => p.concat(c.cells.reduce((pp, cc) => pp.concat(cc), [])), []);
                // if (!state.dragging) {
                //     state.columns = flattenArrayWithObj(renderer.columnsInternal.columns, 'children');
                // }

                if (e.type === 'dragstart') {
                    const node2 = nodes.find(n => n.renderContext && n.renderContext.element === target);
                    e.dataTransfer.setData('text', node2.context.c.id);
                    e.dataTransfer.effectAllowed = 'move';

                    // TODO allow movement anywhere
                    // state.parent = state.columns.find(c => c.children && c.children.find(cc => cc.id === node.context.c.id) != null);
                    // state.columns = !state.parent ? renderer.columnsInternal.columns : state.parent.children;
                    state.dragging = true;
                    state.node = node2;

                    node2.parent.drag.next({which: 'dragstart', drag: node2, drop: null});
                }
                if (e.type === 'dragend') {
                    state.dragging = false;
                    const node2 = nodes.find(n => n.renderContext && n.renderContext.element === target);
                    node2.parent.drag.next({which: 'dragend', drag: node2, drop: null});
                }
                if (e.type === 'dragenter') {
                    const node2 = nodes.find(n => n.renderContext && n.renderContext.element === target);
                    node2.parent.drag.next({which: 'dragenter', drag: state.node, drop: node2});
                }
                if (e.type === 'dragleave') {
                    const node2 = nodes.find(n => n.renderContext && n.renderContext.element === target);
                    node2.parent.drag.next({which: 'dragleave', drag: state.node, drop: node2});
                }
                if (e.type === 'dragover') {
                    const node2 = nodes.find(n => n.renderContext && n.renderContext.element === target);
                    clearDropIndicator(state.dragDropHdl);
                    if (node2.context.c.canBeDroppedOnFn && node2.context.c.canBeDroppedOnFn(state.node, node2)) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        state.dragDropHdl = showDropIndicator(node2, e.pageX);
                    }
                }
                if (e.type === 'drop') {
                    e.preventDefault();
                    // const id = e.dataTransfer.getData('text');
                    const node2 = nodes.find(n => n.renderContext && n.renderContext.element === target);
                    const box = node2.renderContext.element.getBoundingClientRect();
                    const pos = e.pageX >= box.left + box.width / 2 ? 'after' : 'before';

                    const internalFlat = flattenArrayWithObj(renderer.columnsInternal.columns, 'children');
                    const internalDest = internalFlat.find(c => c.children && c.children.find(cc => cc.id === node2.context.c.id) != null).children;
                    const internalSrc = internalFlat.find(c => c.children && c.children.find(cc => cc.id === state.node.context.c.id) != null).children;

                    // TODO revise. temporary so that rows added after movement are in new order
                    const flat = flattenArrayWithObj(renderer.opts.columns, 'children');
                    const dest = flat.find(c => c.children && c.children.find(cc => cc.id === node2.context.c.id) != null).children;
                    const src = flat.find(c => c.children && c.children.find(cc => cc.id === state.node.context.c.id) != null).children;

                    if (node2 !== state.node) {
                        const internalMovement = moveColumn(internalDest, internalSrc, state.node.context.c.id, node2.context.c.id, pos); // TODO allow movement anywhere
                        renderer.columnMovementHistory.push(internalMovement);

                        const movement = moveColumn(dest, src, state.node.context.c.id, node2.context.c.id, pos); // TODO allow movement anywhere
                        renderer.columnMovementHistory.push(movement);

                        syncCellsToColumns(renderer);
                        runScroll(renderer, {which: 'layout'}); // TODO move
                    }
                    clearDropIndicator(state.dragDropHdl);
                    node2.parent.drag.next({
                        which: 'drop',
                        drag: state.node,
                        drop: node2,
                        columnsDest: dest,
                        columnsSrc: src
                    });
                }
            }),
            takeUntil(renderer.done)
        )
        .subscribe();
}

function bindDragResize(node: CellNode, renderer: DivRenderer, resize: HTMLElement): void {
    const done = new Subject();

    fromEvent(resize, 'dblclick')
        .pipe(
            tap((e: MouseEvent) => {
                // e.stopPropagation();
                renderer.autoSizeColumns([node.context.c.id]);
            }),
            takeUntil(renderer.done)
        )
        .subscribe();

    // resize.draggable = true;
    // //TODO unsub
    // // merge(fromEvent(resize, 'dragstart'),
    //     // fromEvent(resize, 'dragend'),
    //     // fromEvent(resize, 'dragenter'),
    //     // fromEvent(resize, 'dragleave'),
    //     // fromEvent(resize, 'dragover'),
    //     // fromEvent(resize, 'drop'))
    // fromEvent(resize, 'dragstart')
    //     .pipe(
    //         tap((e: DragEvent) => {
    //             // e.stopPropagation();
    //             e.preventDefault();
    //         })
    //     )
    //     .subscribe();

    fromEvent(resize, 'mousedown')
        .pipe(
            switchMap((e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                // node.renderContext.element.draggable = false;
                let start = e.clientX;

                return merge(
                    fromEvent(node.parent.renderContext.element, 'mousemove'),
                    fromEvent(node.parent.renderContext.element, 'mouseup')
                )
                    .pipe(
                        tap((ee: MouseEvent) => {
                            // e.stopPropagation();
                            const end = ee.clientX;

                            const w = end - start;
                            start = end;
                            const ncw = calcColumnWidth(node.context.c, true) + w;
                            // console.log(w, ncw);
                            renderer.setColumnWidth(node.context.c.id, ncw);
                            runScroll(renderer, {which: 'layout'});

                            if (ee.type === 'mouseup') {
                                // node.renderContext.element.draggable = true;
                                done.next();
                            }
                        }),
                        takeUntil(done)
                    );
            }),
            takeUntil(renderer.done),
            finalize(() => {
                done.next();
                done.complete();
            })
        )
        .subscribe();
}

function appendCellDragDropChild(node: CellNode, renderer: DivRenderer): void {
    if (renderer.opts.autoFit /*TODO watch for changes and enable/disable on the fly*/ ||
        !node.renderContext ||
        !node.renderContext.element ||
        node.renderContext.element.querySelector('[drag-drop]')) {
        return;
    }

    const hdl = document.createElement('div');
    hdl.setAttribute('drag-drop', '');
    hdl.style.position = 'absolute';
    hdl.style.right = '-4px';
    hdl.style.width = '8px';
    hdl.style.height = '100%';
    hdl.style.cursor = 'col-resize';
    hdl.style.zIndex = '1';
    node.renderContext.element.style.position = 'relative';
    node.renderContext.element.style.overflow = '';
    if (node.renderContext.element.children.length === 0) {
        node.renderContext.element.appendChild(hdl);
    } else {
        node.renderContext.element.insertBefore(hdl, node.renderContext.element.children[0]);
    }

    noZone(() => bindDragDrop(node, renderer));
    noZone(() => bindDragResize(node, renderer, hdl));
}

function removeCellDragDropChild(node: CellNode): void {
    if (!node.renderContext || !node.renderContext.element) return;
    const hdl = node.renderContext.element.querySelector('[drag-drop]');
    if (!hdl) return;
    node.renderContext.element.removeChild(hdl);
}

function syncColumn(c: Column, cols: Array<Column>): void {
    const cc = cols.find(e => e.id === c.id);
    if (cc) {
        c.name = cc.name;
        c.width = cc.width;
        c.height = cc.height;
        c.compareFn = cc.compareFn;
        c.canBeDroppedOnFn = cc.canBeDroppedOnFn;
        c.sticky = cc.sticky;
        c.hidden = cc.hidden;
        c.ctx = cc.ctx;

        c.willMeasure = cc.willMeasure;
        c.didMeasure = cc.didMeasure;
        c.measureElement = cc.measureElement;
    }
}

function autoSizeColumnRow(row: RowNode, i: number, rows: Array<RowNode>, renderer: DivRenderer, onlyColumns?: Array<Column>): Promise<any> {
    const cells = row.cells.children
        .filter(ee => !ee.context.c.hidden && (!onlyColumns || onlyColumns.some(eee => eee.id === ee.context.c.id)));
    return Promise
        .all(cells.map(ee => {
            const cw = i === 0 ? null : calcColumnWidth(ee.context.c, true);
            return renderer.autoSizeColumns([ee.context.c.id], true)
                .then((aa: any) => {
                    if (cw != null && aa.ncw != null && cw > aa.ncw) {
                        renderer.setColumnWidth(ee.context.c.id, cw);
                    }
                });
        }))
        .then(() => {
            if (++i !== rows.length) {
                return autoSizeColumnRow(rows[i], i, rows, renderer, onlyColumns);
            }
        });
}

export function autoSizeColumnTree(rows: Array<RowNode>, renderer: DivRenderer, onlyColumns?: Array<Column>): Promise<any> {
    if (!rows) {
        return Promise.resolve();
    }
    const rr = [...rows].reverse().filter(e => !e.def.hidden);
    if (rr.length === 0) {
        return Promise.resolve();
    }
    return autoSizeColumnRow(rr[0], 0, rr, renderer, onlyColumns).then(() => renderer.layout());
}

function layoutColumns(renderer: DivRenderer): void {
    const node = renderer.gridNode;

    if (renderer.columnsInternal && renderer.contentColumns) {
        const cols = flattenArrayWithObj<Column>(renderer.opts.columns, 'children');
        flattenArrayWithObj<Column>(renderer.columnsInternal.columns, 'children').forEach(c => syncColumn(c, cols));
        flattenArrayWithObj<Column>(renderer.columnsInternal.left, 'children').forEach(c => syncColumn(c, cols));
        flattenArrayWithObj<Column>(renderer.columnsInternal.right, 'children').forEach(c => syncColumn(c, cols));

        // const rows = flattenObj<NodeWithRows, RowNode>(renderer.contentColumns.columns, 'children');
        // rows.forEach((r, i) => {
        //     r.cells.filter(l => !!l.renderContext).forEach(l => {
        //         const width = calcCellWidth(l.context.c.id, renderer.columnsInternal.columns, renderer.columnsInternal.left, renderer.columnsInternal.right, l.context.c.sticky, true);
        //         l.renderContext.element.style.width = `${width}px`;
        //         // l.renderContext.element.style.zIndex = `${rows.length - i}`;
        //         if (l.context.c.rowSpan > 1) {
        //             l.renderContext.element.style.height = `${sumContentHeight(rows.slice(i, l.context.c.rowSpan))}px`;
        //         }
        //     });
        // });

        return;
    }

    renderer.columnsInternal = {
        columns: reconstructColumns(renderer.opts.columns, null),
        left: reconstructColumns(renderer.opts.columns, 'left'),
        right: reconstructColumns(renderer.opts.columns, 'right')
    };

    renderer.contentColumns = {
        columns: columnRows(node, renderer, renderer.columnsInternal.columns),
        left: columnRows(node, renderer, renderer.columnsInternal.left),
        right: columnRows(node, renderer, renderer.columnsInternal.right)
    };

    renderer.contentColumns.left.forEach(n => n.sticky = 'left');
    renderer.contentColumns.right.forEach(n => n.sticky = 'right');
}

function layoutRect(renderer: DivRenderer): void {

    const node = renderer.gridNode;
    const columns = renderer.columns;
    const content = renderer.content;
    const contentTop = renderer.contentTop;
    const contentBottom = renderer.contentBottom;
    const scrollBarWidth = renderer.scrollBarWidth;
    const scrollContent = renderer.scrollContent;
    const scrollVp = renderer.scrollVp;

    // const width = renderer.columns.filter(c => c.sticky !== 'left' && c.sticky !== 'right').reduce((p, c) => p + calcColumnWidth(c), 0);
    // const width = renderer.columnsInternal.columns.reduce((p, c) => p + calcColumnWidth(c), 0);
    // const width = renderer.columnsInternal.columns.filter(c => !c.hidden).reduce((p, c) => p + calcColumnWidth(c), 0);
    const width = renderer.columnsInternal.columns.reduce((p, c) => p + calcColumnWidth(c, true), 0);
    columns.content.style.width = `${width}px`;
    content.content.style.width = `${width}px`;
    contentTop.content.style.width = `${width}px`;
    contentBottom.content.style.width = `${width}px`;
    scrollContent.style.width = `${width}px`;

    // const columnsHeight = renderer.opts.columns.reduce((p, c) => Math.max(p, calcColumnHeight(c)), 0);
    const columnsHeight = sumContentHeight(renderer.contentColumns.columns); // TODO left right hidden
    columns.left.viewport.style.height = columns.left.content.style.height = `${columnsHeight}px`;
    columns.right.viewport.style.height = columns.right.content.style.height = `${columnsHeight}px`;
    columns.content.style.height = `${columnsHeight}px`;
    columns.viewport.style.height = `calc(100% + ${scrollBarWidth}px)`;
    columns.root.style.height = `${columnsHeight}px`;

    // const height = ((rows.length - (content.viewport.clientWidth >= width ? 1 : 0)) * 25);
    // const height = renderer.rows.length * 25;
    const height = sumContentHeight(renderer.rows);
    content.left.viewport.style.height = content.left.content.style.height = `${height}px`;
    content.right.viewport.style.height = content.right.content.style.height = `${height}px`;
    content.content.style.height = `${height}px`;
    content.viewport.style.height = `calc(100% + ${scrollBarWidth}px)`;
    content.root.style.height = `${height}px`;

    // const topHeight = node.rowsTop.length * 25;
    const topHeight = sumContentHeight(node.rowsTop);
    contentTop.left.viewport.style.height = `${topHeight}px`;
    contentTop.right.viewport.style.height = `${topHeight}px`;
    contentTop.viewport.style.height = `calc(100% + ${scrollBarWidth}px)`;
    contentTop.root.style.height = `${topHeight}px`;

    // const bottomHeight = node.rowsBottom.length * 25;
    const bottomHeight = sumContentHeight(node.rowsBottom);
    contentBottom.left.viewport.style.height = `${bottomHeight}px`;
    contentBottom.right.viewport.style.height = `${bottomHeight}px`;
    contentBottom.viewport.style.height = `calc(100% + ${scrollBarWidth}px)`;
    contentBottom.root.style.height = `${bottomHeight}px`;

    ////////////////////

    // const leftWidth = renderer.columnsInternal.left.reduce((p, c) => p + calcColumnWidth(c), 0);
    // const leftWidth = renderer.columnsInternal.left.filter(c => !c.hidden).reduce((p, c) => p + calcColumnWidth(c), 0);
    const leftWidth = renderer.columnsInternal.left.reduce((p, c) => p + calcColumnWidth(c, true), 0);
    columns.left.viewport.style.width = columns.left.content.style.width = `${leftWidth}px`;
    columns.left.viewport.style.minWidth = columns.left.content.style.minWidth = `${leftWidth}px`;
    columns.left.viewport.style.maxWidth = columns.left.content.style.maxWidth = `${leftWidth}px`;
    content.left.viewport.style.width = content.left.content.style.width = `${leftWidth}px`;
    content.left.viewport.style.minWidth = content.left.content.style.minWidth = `${leftWidth}px`;
    content.left.viewport.style.maxWidth = content.left.content.style.maxWidth = `${leftWidth}px`;
    contentTop.left.viewport.style.width = contentTop.left.content.style.width = `${leftWidth}px`;
    contentTop.left.viewport.style.minWidth = contentTop.left.content.style.minWidth = `${leftWidth}px`;
    contentTop.left.viewport.style.maxWidth = contentTop.left.content.style.maxWidth = `${leftWidth}px`;
    contentBottom.left.viewport.style.width = contentBottom.left.content.style.width = `${leftWidth}px`;
    contentBottom.left.viewport.style.minWidth = contentBottom.left.content.style.minWidth = `${leftWidth}px`;
    contentBottom.left.viewport.style.maxWidth = contentBottom.left.content.style.maxWidth = `${leftWidth}px`;

    ////////////////////

    // const rightWidth = renderer.columnsInternal.right.reduce((p, c) => p + calcColumnWidth(c), 0);
    // const rightWidth = renderer.columnsInternal.right.filter(c => !c.hidden).reduce((p, c) => p + calcColumnWidth(c), 0);
    const rightWidth = renderer.columnsInternal.right.reduce((p, c) => p + calcColumnWidth(c, true), 0);
    columns.right.viewport.style.width = columns.right.content.style.width = `${rightWidth}px`;
    columns.right.viewport.style.minWidth = columns.right.content.style.minWidth = `${rightWidth}px`;
    columns.right.viewport.style.maxWidth = columns.right.content.style.maxWidth = `${rightWidth}px`;
    content.right.viewport.style.width = content.right.content.style.width = `${rightWidth}px`;
    content.right.viewport.style.minWidth = content.right.content.style.minWidth = `${rightWidth}px`;
    content.right.viewport.style.maxWidth = content.right.content.style.maxWidth = `${rightWidth}px`;
    contentTop.right.viewport.style.width = contentTop.right.content.style.width = `${rightWidth}px`;
    contentTop.right.viewport.style.minWidth = contentTop.right.content.style.minWidth = `${rightWidth}px`;
    contentTop.right.viewport.style.maxWidth = contentTop.right.content.style.maxWidth = `${rightWidth}px`;
    contentBottom.right.viewport.style.width = contentBottom.right.content.style.width = `${rightWidth}px`;
    contentBottom.right.viewport.style.minWidth = contentBottom.right.content.style.minWidth = `${rightWidth}px`;
    contentBottom.right.viewport.style.maxWidth = contentBottom.right.content.style.maxWidth = `${rightWidth}px`;

    ////////////////////

    content.boundBox.style.width = `${leftWidth + width + rightWidth}px`;

    scrollVp.style.marginLeft = `${leftWidth}px`;
    scrollVp.style.marginRight = `${rightWidth}px`;
    const viewportWidth = content.viewport.getBoundingClientRect().width;
    // scrollVp.style.width = content.viewport.clientWidth + 'px';
    scrollVp.style.width = `${viewportWidth}px`;

    ////////////////////

    // const bodyHeight = renderer.opts.height - scrollVp.getBoundingClientRect().height - columnsHeight - topHeight - bottomHeight;
    // const bodyHeight = renderer.opts.height - (scrollVp.clientHeight + columnsHeight + topHeight + bottomHeight);
    // const bodyHeight = node.renderContext.element.clientHeight - scrollVp.getBoundingClientRect().height - columnsHeight - topHeight - bottomHeight;
    const bodyHeight = (renderer.opts.height || node.renderContext.element.clientHeight) - scrollVp.getBoundingClientRect().height - columnsHeight - topHeight - bottomHeight;
    // columnsVp.style.paddingRight = (height <= bodyHeight ? 0 : scrollBarWidth) + 'px'; // commented to use overflow auto instead
    // contentVp.style.height = (bodyHeight - (bodyHeight % 25)) + 'px';
    // contentVp.style.maxHeight = (bodyHeight - (bodyHeight % 25)) + 'px';
    // contentVp.style.height = (bodyHeight - (bodyHeight % (height / renderer.rows.length))) + 'px';
    // contentVp.style.maxHeight = (bodyHeight - (bodyHeight % (height / renderer.rows.length))) + 'px';
    renderer.contentVp.style.maxHeight = `${bodyHeight}px`;

    const overflow = height <= bodyHeight ? 'hidden' : 'scroll';
    renderer.columnsVp.style.overflowY = overflow;
    renderer.contentTopVp.style.overflowY = overflow;
    renderer.contentBottomVp.style.overflowY = overflow;

    renderer.contentTopVp.style.display = topHeight === 0 ? 'none' : 'flex';
    renderer.contentBottomVp.style.display = bottomHeight === 0 ? 'none' : 'flex';
    renderer.scrollVp.style.display = width <= viewportWidth ? 'none' : 'block';
}

function layoutCells(
    renderer: DivRenderer,
    node: RowNode,
    toRemove: Array<CellNode>,
    toRender: Array<CellNode>,
    currentContent: HTMLElement,
    lastRendered: Array<CellNode>,
    content: HTMLElement
): Array<CellNode> {
    const isColumnRow = node.def.id.startsWith('column');
    toRemove && toRemove.forEach(n => {
        try {
            // if (isColumnRow) {
            //     removeCellDragDropChild(n);
            // }
            renderer.rr.removeChild(node, n);
            renderer.destroy(n);
        } catch (e) {
            console.log(e);
        }
    });
    if (toRender.length === 0) return toRender;
    const current = Array.from(currentContent.children)
        .map(e => {
            return toRender.find(n => n.renderContext && n.renderContext.element === e);
        })
        .filter(n => !!n);
    const visibleIndexStart = node.cells.indexOf(toRender[0]);
    const visibleIndexEnd = node.cells.indexOf(toRender[toRender.length - 1]);
    const contentColumnsMostRows = [
        renderer.contentColumns.left,
        renderer.contentColumns.right,
        renderer.contentColumns.columns
    ].sort((a, b) => b.length - a.length)[0];
    toRender.forEach((n, i) => {
        try {
            const wasNotRendered = !n.renderContext || (lastRendered && lastRendered.indexOf(n) === -1);
            layoutCellNode(n, content, renderer, i, toRender, current, node.cells.indexOf(n), node.cells.length - 1, visibleIndexStart, visibleIndexEnd);
            if (wasNotRendered || isColumnRow) {
                if (isColumnRow) {
                    const index = node.sticky === 'left'
                        ? renderer.contentColumns.left.children.indexOf(node)
                        : node.sticky === 'right'
                            ? renderer.contentColumns.right.children.indexOf(node)
                            : renderer.contentColumns.columns.children.indexOf(node);
                    const hook = () => {
                        const width = calcCellWidth(n.context.c.id, renderer.columnsInternal.columns, renderer.columnsInternal.left, renderer.columnsInternal.right, n.context.c.sticky, true);
                        n.renderContext.element.style.width = `${width}px`;
                        if (width === 0) {
                            n.renderContext.element.style.display = 'none';
                        }
                        n.renderContext.element.style.zIndex = `${renderer.contentColumns.columns.length - index}`;
                        n.renderContext.element.style.position = 'relative';
                        if (n.context.c.rowSpan > 1) {
                            n.renderContext.element.style.height = `${sumContentHeight(contentColumnsMostRows.children.slice(index, n.context.c.rowSpan))}px`; // TODO flatten? expensive?
                        }
                        if (n.context.c.cellSpan > 1) {
                            // TODO z-index? negative margin-left? for overlapped cells
                            n.renderContext.element.style.width = `${sumContentWidth(node.cells.children.slice(index, n.context.c.cellSpan))}px`; // TODO flatten? expensive?
                        }

                        // if (wasNotRendered) {
                        //     appendCellDragDropChild(n, renderer);
                        // }
                    };
                    getElement2(n, null).then(() => hook());
                }
                n.detectChanges({onlySelf: true});
            }
        } catch (e) {
            console.log(e);
        }
    });
    return toRender;
}

function layoutRows(
    renderer: DivRenderer,
    rr: Renderer,
    node: NodeWithRows,
    toRemove: Array<RowNode>,
    toRender: Array<RowNode>,
    currentContent: HTMLElement,
    lastRendered: Array<RowNode>,
    content: ContentDiv | ContentAdjacentDiv
): Array<RowNode> {
    toRemove && toRemove.forEach(n => {
        try {
            const rleft = getrow(n.def, 'left', renderer);
            if (rleft) {
                const rleftmoveback = rleft.cells.filter(l => !hasStickyColumn(l.context.c, 'left'));
                rleftmoveback.forEach(l => n.cells.renderContext.element.appendChild(l.renderContext.element));
                if (rleft.renderContext && rleft.renderContext.element.parentElement) {
                    renderer.content.left.content.removeChild(rleft.renderContext.element);
                }
                renderer.destroy(rleft);
                renderer.rowcache.left.splice(renderer.rowcache.left.indexOf(rleft), 1);
            }

            const rright = getrow(n.def, 'right', renderer);
            if (rright) {
                const rrightmoveback = rright.cells.filter(l => !hasStickyColumn(l.context.c, 'right'));
                rrightmoveback.forEach(l => n.cells.renderContext.element.appendChild(l.renderContext.element));
                if (rright.renderContext && rright.renderContext.element.parentElement) {
                    renderer.content.right.content.removeChild(rright.renderContext.element);
                }
                renderer.destroy(rright);
                renderer.rowcache.right.splice(renderer.rowcache.right.indexOf(rright), 1);
            }

            renderer.rr.removeChild(node, n);
            renderer.destroy(n);
        } catch (e) {
            console.log(e);
        }
    });
    const toRenderFlatten = toRender ? toRender : flattenObj<NodeWithRows, RowNode>(node, 'children');
    if (toRenderFlatten.length === 0) return toRenderFlatten;
    const current = Array.from(currentContent.children)
        .map(e => {
            return toRenderFlatten.find(n => n.renderContext && n.renderContext.element === e);
        })
        .filter(n => !!n);
    const rlcurrent = Array.from(renderer.content.left.content.children)
        .map(e => {
            return renderer.rowcache.left.find(n => n.renderContext && n.renderContext.element === e);
        })
        .filter(n => !!n);
    const rrcurrent = Array.from(renderer.content.right.content.children)
        .map(e => {
            return renderer.rowcache.right.find(n => n.renderContext && n.renderContext.element === e);
        })
        .filter(n => !!n);
    const visibleIndexStart = renderer.rows.indexOf(toRenderFlatten[0]);
    const visibleIndexEnd = renderer.rows.indexOf(toRenderFlatten[toRenderFlatten.length - 1]);
    toRenderFlatten.forEach((n, i) => {
        try {
            const wasNotRendered = !n.renderContext || (lastRendered && lastRendered.indexOf(n) === -1);
            const isColumnRow = n.def.id.startsWith('column');
            const index = (n.sticky === 'left' ? renderer.rowcache.left : n.sticky === 'right' ? renderer.rowcache.right : renderer.rows).indexOf(n);
            const lastIndex = (n.sticky === 'left' ? renderer.rowcache.left : n.sticky === 'right' ? renderer.rowcache.right : renderer.rows).length - 1;

            layoutRowNode(n, content, renderer, rr, i, current, index, lastIndex, visibleIndexStart, visibleIndexEnd);

            const rleft = getrow(n.def, 'left', renderer, n);
            const rright = getrow(n.def, 'right', renderer, n);
            if (!isColumnRow && (renderer.columnsInternal.left.length !== 0 || renderer.columnsInternal.right.length !== 0)) {
                Promise.all([
                    layoutRowNodeAdjacent(n, rleft, (content as ContentDiv).left, renderer, rr, i, rlcurrent, index, lastIndex, visibleIndexStart, visibleIndexEnd, 'left'),
                    layoutRowNodeAdjacent(n, rright, (content as ContentDiv).right, renderer, rr, i, rrcurrent, index, lastIndex, visibleIndexStart, visibleIndexEnd, 'right')
                ]).then(moveback => {
                    if (moveback.reduce((p, c) => p += c, 0) !== 0) {
                        n.cells.sort([null], l => rleft.cells.indexOf(l) === -1 && rright.cells.indexOf(l) === -1, {emitEvent: false});
                    }
                    if (wasNotRendered) {
                        rleft.detectChanges({onlySelf: true});
                        rright.detectChanges({onlySelf: true});
                    }
                });
            }

            if (wasNotRendered || isColumnRow) {
                if (isColumnRow) {
                    const hook = () => {
                        n.renderContext.element.style.zIndex = `${renderer.contentColumns.columns.length - i}`; // TODO flatten? expensive?
                        n.renderContext.element.style.position = 'relative';
                    };
                    getElement2(n, null).then(() => hook());
                }
                n.detectChanges({onlySelf: true});
            }
        } catch (e) {
            console.log(e);
        }
    });
    return toRenderFlatten;
}

function setScroll(renderer: DivRenderer): void {
    const content = renderer.content;
    const contentVp = renderer.contentVp;

    // const y = `translateY(${contentVp.scrollTop - renderer.scrollState.lastPadTop}px`;
    // content.boundBox.style.transform = `translateY(${contentVp.scrollTop}px)`;
    const y = `translateY(${renderer.scrollState.lastPadTop}px)`;
    content.boundBox.style.transform = `translateY(${renderer.scrollState.scrollTop}px)`;
    content.left.content.style.transform = y;
    content.right.content.style.transform = y;
    content.content.style.transform = y;
}

interface RowScrollState {
    lastPadLeft?: number;
    visibleCellsIndexStart?: number;
    visibleCellsIndexEnd?: number;
    visibleCellsIndexStartPadded?: number;
    visibleCellsIndexEndPadded?: number;
    lastSeen?: Array<CellNode>;
}

interface GridScrollState {
    lastPadTop?: number;
    visibleRowsIndexStart?: number;
    visibleRowsIndexEnd?: number;
    visibleRowsIndexStartPadded?: number;
    visibleRowsIndexEndPadded?: number;
    lastSeen?: Array<RowNode>;
    // lastSeenStickyRowsTop?: Array<RowNode>;
    // lastSeenStickyRowsBottom?: Array<RowNode>;
    offset?: number;

    rowScrollStates: { [key: string]: RowScrollState; };

    scrollTop?: number;
    clientHeight?: number;

    scrollLeft?: number;
    clientWidth?: number;
}

function applyCellScrollState(renderer: DivRenderer, node: RowNode): void {
    const id = node.sticky != null ? `${node.def.id}-${node.sticky}` : node.def.id;
    const state = renderer.scrollState.rowScrollStates[id];
    // const scrollPadding = Math.min((renderer.opts.horizontalScrollPadding || 2) / 2, 1);
    const scrollPadding = renderer.opts.horizontalScrollPadding || 4;

    const cells = node.sticky != null ? node.cells.children : node.cells.filter(n => n.context.c.sticky == null && !n.context.c.hidden);

    const avgCellWidth = avgContentCellWidth(cells);
    const startIndex = Math.ceil(renderer.scrollState.scrollLeft / avgCellWidth);
    const numVisible = numCellsVisibleInWidth(cells.slice(startIndex), renderer.scrollState.clientWidth);
    state.visibleCellsIndexStart = startIndex;
    state.visibleCellsIndexEnd = state.visibleCellsIndexStart + numVisible.num;

    let shift;
    if (numVisible.width < renderer.scrollState.clientWidth) {
        shift = numCellsVisibleInWidth(cells.slice(Math.max(0, state.visibleCellsIndexStart - numVisible.num), state.visibleCellsIndexStart), renderer.scrollState.clientWidth - numVisible.width);
        state.visibleCellsIndexStart -= shift.num;
    }

    state.visibleCellsIndexStartPadded = Math.max(0, state.visibleCellsIndexStart - scrollPadding);
    state.visibleCellsIndexEndPadded = Math.min(cells.length, state.visibleCellsIndexEnd + scrollPadding);

    // state.lastPadLeft = sumContentWidth(cells.slice(state.visibleCellsIndexStartPadded, state.visibleCellsIndexStart));
    const skippedLeft = Math.max(state.visibleCellsIndexStartPadded, 0);
    state.lastPadLeft = skippedLeft === 0 ? 0 : sumContentWidth(cells.slice(0, skippedLeft));
    // if (numVisible.width < contentVp.clientWidth) {
    //     state.lastPadLeft += shift.width - (contentVp.clientWidth - numVisible.width);
    // }
}

function applyRowScrollState(renderer: DivRenderer): void {
    const state = renderer.scrollState;
    const scrollPadding = renderer.opts.verticalScrollPadding || 4;
    // const contentVp = renderer.contentVp;

    const avgRowHeight = avgContentRowHeight(renderer.rows);

    const startIndex = Math.ceil(state.scrollTop / avgRowHeight);
    const numVisible = numRowsVisibleInHeight(renderer.rows.slice(startIndex), state.clientHeight);
    state.visibleRowsIndexStart = startIndex;
    state.visibleRowsIndexEnd = state.visibleRowsIndexStart + numVisible.num;

    let shift;
    if (numVisible.height < state.clientHeight) {
        shift = numRowsVisibleInHeight(renderer.rows.slice(Math.max(0, state.visibleRowsIndexStart - numVisible.num), state.visibleRowsIndexStart), state.clientHeight - numVisible.height);
        state.visibleRowsIndexStart -= shift.num;
    }

    state.visibleRowsIndexStartPadded = Math.max(0, state.visibleRowsIndexStart - scrollPadding);
    state.visibleRowsIndexEndPadded = Math.min(renderer.rows.length, state.visibleRowsIndexEnd + scrollPadding);

    // state.lastPadTop = sumContentHeight(renderer.rows.slice(state.visibleRowsIndexStartPadded, state.visibleRowsIndexStart));
    // if (numVisible.height < state.clientHeight) {
    //     state.lastPadTop += shift.height - (state.clientHeight - numVisible.height);
    // }
    const skippedTop = Math.max(state.visibleRowsIndexStartPadded, 0);
    state.lastPadTop = skippedTop === 0 ? 0 : sumContentHeight(renderer.rows.slice(0, skippedTop));
}

function runCellScroll(renderer: DivRenderer, node: RowNode): void {
    const id = node.sticky != null ? `${node.def.id}-${node.sticky}` : node.def.id;
    if (!renderer.scrollState.rowScrollStates[id]) {
        renderer.scrollState.rowScrollStates[id] = {};
    }
    const state = renderer.scrollState.rowScrollStates[id];
    // const contentVp = renderer.content.viewport;

    const cells = node.sticky != null ? node.cells.children : node.cells.filter(n => n.context.c.sticky == null && !n.context.c.hidden);

    if (node.sticky == null) {
        applyCellScrollState(renderer, node);
        // if (isNaN(state.visibleCellsIndexStart)) {
        // if (id.startsWith('column')) {
        //     console.log(id, state);
        // }
    }

    const nextSeen = cells.slice(state.visibleCellsIndexStartPadded, state.visibleCellsIndexEndPadded);
    const toRemove = !state.lastSeen ? [] : state.lastSeen.filter(n => nextSeen.indexOf(n) === -1);

    getElement2(node, null)
        .then(element => {
            state.lastSeen = layoutCells(renderer, node, toRemove, nextSeen, element, state.lastSeen, element);
        });

    if (node.sticky == null) {
        // const x = `translateX(${contentVp.scrollLeft - state.lastPadLeft}px)`;
        const x = `translateX(${state.lastPadLeft}px)`;
        node.renderContext.element.style.transform = x;
    }
}

function runRowScroll(renderer: DivRenderer): void {
    const state = renderer.scrollState;
    const node = renderer.gridNode;
    const columns = renderer.columns;
    const content = renderer.content;
    const contentVp = renderer.contentVp;
    const contentTop = renderer.contentTop;
    const contentBottom = renderer.contentBottom;

    applyRowScrollState(renderer);

    const nextSeen = renderer.rows.slice(state.visibleRowsIndexStartPadded, state.visibleRowsIndexEndPadded);
    const toRemove = !state.lastSeen ? [] : state.lastSeen.filter(n => nextSeen.indexOf(n) === -1);

    if (renderer.scrollState.offset > 0) {
        // if (toRemove.length > 0) {
        const viewHeight = sumContentHeight(renderer.rows.slice(state.visibleRowsIndexStartPadded, state.visibleRowsIndexEndPadded));
        // const offset = sumContentHeight(nextSeen.slice(0, renderer.scrollState.offset));
        // const offset = sumContentHeight(nextSeen.slice(0, toRemove.length));
        // if (contentVp.scrollTop + contentVp.clientHeight > contentVp.scrollHeight) {
        if (contentVp.scrollTop + contentVp.clientHeight > viewHeight) {
            contentVp.scrollTop -= (contentVp.scrollTop + contentVp.clientHeight) - viewHeight;
        }
        renderer.scrollState.offset = 0;
    }

    state.lastSeen = layoutRows(renderer, renderer.rr, node, toRemove, nextSeen, content.content, state.lastSeen, content);

    layoutRows(renderer, renderer.rr, node.rowsTop, null, null, contentTop.content, null, contentTop);
    layoutRows(renderer, renderer.rr, node.rowsBottom, null, null, contentBottom.content, null, contentBottom);

    layoutRows(renderer, renderer.rr, renderer.contentColumns.columns, null, null, columns.content, null, columns);
    layoutRows(renderer, renderer.rr, renderer.contentColumns.left, null, null, columns.left.content, null, columns.left);
    layoutRows(renderer, renderer.rr, renderer.contentColumns.right, null, null, columns.right.content, null, columns.right);
}

function runScroll(renderer: DivRenderer, event: LayoutEvent): Promise<void> {
    const className = document.activeElement.className;
    let cell = document.activeElement;
    while (cell && !(cell.hasAttribute('wrapper') && cell.hasAttribute('cell'))) {
        cell = cell.parentElement;
    }
    const columnId = cell ? cell.getAttribute('column-id') : null;

    const node = renderer.gridNode;
    const columns = renderer.columns;
    const contentVp = renderer.contentVp;
    const contentTop = renderer.contentTop;
    const contentBottom = renderer.contentBottom;

    layoutColumns(renderer);

    if (event.which === 'layout') {
        renderer.rowcache.left.sort((a, b) => renderer.rows.findIndex(n => n.def.id === a.def.id) - renderer.rows.findIndex(n => n.def.id === b.def.id));
        renderer.rowcache.right.sort((a, b) => renderer.rows.findIndex(n => n.def.id === a.def.id) - renderer.rows.findIndex(n => n.def.id === b.def.id));
    }

    const ch = parseInt(columns.root.style.height);
    const th = parseInt(contentTop.root.style.height);
    const bh = parseInt(contentBottom.root.style.height);

    layoutRect(renderer);

    const columnsHeight = sumContentHeight(renderer.contentColumns.columns);
    const topHeight = sumContentHeight(node.rowsTop);
    const bottomHeight = sumContentHeight(node.rowsBottom);

    if (ch > columnsHeight) {
        contentVp.scrollTop -= Math.max(0, ch - columnsHeight);
    }
    if (th > topHeight) {
        contentVp.scrollTop -= Math.max(0, th - topHeight);
    }
    if (bh > bottomHeight) {
        contentVp.scrollTop -= Math.max(0, bh - bottomHeight);
    }

    renderer.scrollState.scrollTop = renderer.contentVp.scrollTop;
    renderer.scrollState.clientHeight = renderer.contentVp.clientHeight;
    renderer.scrollState.scrollLeft = renderer.content.viewport.scrollLeft;
    renderer.scrollState.clientWidth = renderer.content.viewport.clientWidth;

    if (event.which === 'scroll' || event.which === 'layout') {
        runRowScroll(renderer);
    }

    if (event.which === 'scroll-horizontal') {
        renderer.rows.filter(n => n.renderContext).forEach(n => runCellScroll(renderer, n));

        renderer.contentColumns.columns.forEach(n => runCellScroll(renderer, n));
        renderer.contentColumns.left.forEach(n => runCellScroll(renderer, n));
        renderer.contentColumns.right.forEach(n => runCellScroll(renderer, n));
    }

    setScroll(renderer);

    if (event.which === 'layout') {
        renderer.rowcache.left.sort((a, b) => renderer.rows.findIndex(n => n.def.id === a.def.id) - renderer.rows.findIndex(n => n.def.id === b.def.id));
        renderer.rowcache.right.sort((a, b) => renderer.rows.findIndex(n => n.def.id === a.def.id) - renderer.rows.findIndex(n => n.def.id === b.def.id));
    }

    if (className && columnId) {
        scheduled(of(null), animationFrameScheduler).pipe(
            tap(() => {
                const selector = `[wrapper][cell][column-id="${columnId}"] .${className.split(' ').join('.')}`;
                const el = node.renderContext
                    && node.renderContext.element
                    && node.renderContext.element.querySelector(selector);
                if (el) {
                    el && el['focus'] && (el as HTMLElement).focus();
                }
            }),
            take(1)
        ).subscribe();
    }

    renderer.layoutt.next(event.which);

    return Promise.resolve();
}

function bindScroll(renderer: DivRenderer): void {
    fromEvent(renderer.contentVp, 'scroll')
        .pipe(
            auditTime(0, animationFrameScheduler),
            tap(() => runScroll(renderer, {which: 'scroll'})),
            takeUntil(renderer.done)
        )
        .subscribe();

    [
        {el: renderer.columns.viewport, which: 'columns', firing: false},
        {el: renderer.content.viewport, which: 'content', firing: false},
        {el: renderer.contentTop.viewport, which: 'contentTop', firing: false},
        {el: renderer.contentBottom.viewport, which: 'contentBottom', firing: false},
        {el: renderer.scrollVp, which: 'scrollVp', firing: false}
    ].forEach((pair, i, arr) => {
        fromEvent(pair.el, 'scroll')
            .pipe(
                filter(() => {
                    const firing = arr.find(e => e.firing);
                    return !firing || firing === pair;
                }),
                auditTime(0, animationFrameScheduler),
                tap(() => {
                    pair.firing = true;
                    arr.filter(ee => ee.el !== pair.el && ee.el.scrollLeft !== pair.el.scrollLeft).forEach(ee => ee.el.scrollLeft = pair.el.scrollLeft);
                    runScroll(renderer, {which: 'scroll-horizontal'});
                }),
                debounceTime(100),
                tap(() => pair.firing = false),
                takeUntil(renderer.done)
            )
            .subscribe();
    });

    /*fromEvent(window, 'resize')
        .pipe(
            takeUntil(renderer.done),
            auditTime(0, animationFrameScheduler),
            tap(() => {
                layoutRect(renderer);
                setScroll(renderer);
            })
        )
        .subscribe();*/

    const resizeObserver = window['ResizeObserver'] || ResizeObserver;
    let hasAdjustedWidth = false;
    let ro;
    fromEventPattern(handler => {
        ro = new resizeObserver(handler);
        ro.observe(renderer.gridNode.renderContext.element);
    }, () => {
        ro.disconnect();
    })
        .pipe(
            auditTime(0, animationFrameScheduler),
            tap(() => {
                if (renderer.opts.autoFit) {
                    const ccw = renderer.columns.content.getBoundingClientRect().width;
                    let width = renderer.gridNode.renderContext.element.getBoundingClientRect().width;

                    if (renderer.opts.autoFitMinWidth != null) {
                        width = Math.max(width, renderer.opts.autoFitMinWidth);
                    }

                    width = Math.floor(width);

                    let numRemovedCols = 0;

                    if (renderer.opts.useStandardAutofit) {
                        for (const col of renderer.opts.columns) {
                            if (col.hidden) {
                                numRemovedCols += 1;
                            }

                            if (col.setWidth != null && !col.hidden) {
                                numRemovedCols += 1;
                                width = width - col.setWidth - 1;
                            }
                        }
                    }

                    renderer.opts.columns.forEach(e => {
                        if (renderer.opts.useStandardAutofit && e.hidden) {
                            return renderer.setColumnWidth(e.id, 0);
                        }

                        if (e.setWidth != null) {
                            return renderer.setColumnWidth(e.id, e.setWidth);
                        }

                        const colWidth = renderer.opts.useStandardAutofit ?
                            Math.floor(width / (renderer.opts.columns.length - numRemovedCols)) :
                            calcColumnWidth(e, true) / ccw * width;

                        renderer.setColumnWidth(e.id, colWidth);
                    });

                    runScroll(renderer, {which: 'layout'});
                } else {
                    layoutRect(renderer);
                    setScroll(renderer);
                }

                renderer.onResize.next();
            }),
            takeUntil(renderer.done)
        )
        .subscribe();
}

function decorateDivGrid(node: GridNode, renderer: DivRenderer) {
    const columns = createContentDiv();
    const columnsVp = createRootViewport();
    columnsVp.setAttribute('columns', '');
    columnsVp.style.overflow = 'hidden';
    columnsVp.appendChild(columns.boundBox);
    columnsVp.appendChild(columns.left.viewport);
    columnsVp.appendChild(columns.root);
    columnsVp.appendChild(columns.right.viewport);

    const content = createContentDiv();
    const contentVp = createRootViewport();
    contentVp.setAttribute('content', '');
    contentVp.style.overflowX = 'hidden';
    contentVp.style.overflowY = 'auto';
    contentVp.appendChild(content.boundBox);
    contentVp.appendChild(content.left.viewport);
    contentVp.appendChild(content.root);
    contentVp.appendChild(content.right.viewport);

    const contentTop = createContentDiv();
    const contentTopVp = createRootViewport();
    contentTopVp.setAttribute('content-top', '');
    contentTopVp.appendChild(contentTop.boundBox);
    contentTopVp.appendChild(contentTop.left.viewport);
    contentTopVp.appendChild(contentTop.root);
    contentTopVp.appendChild(contentTop.right.viewport);

    const contentBottom = createContentDiv();
    const contentBottomVp = createRootViewport();
    contentBottomVp.setAttribute('content-bottom', '');
    contentBottomVp.appendChild(contentBottom.boundBox);
    contentBottomVp.appendChild(contentBottom.left.viewport);
    contentBottomVp.appendChild(contentBottom.root);
    contentBottomVp.appendChild(contentBottom.right.viewport);

    ////////////////////

    const scrollBarWidth = getScrollBarWidth();

    const scrollContent = document.createElement('div');
    scrollContent.setAttribute('content', '');
    scrollContent.style.height = '1px';
    scrollContent.innerHTML = '&nbsp;';

    const scrollVp = document.createElement('div');
    scrollVp.setAttribute('viewport', '');
    scrollVp.setAttribute('scroll', '');
    scrollVp.style.width = '100%';
    scrollVp.style.overflowX = 'auto';
    scrollVp.style.overflowY = 'hidden';
    scrollVp.appendChild(scrollContent);

    ////////////////////

    renderer.columns = columns;
    renderer.columnsVp = columnsVp;
    renderer.content = content;
    renderer.contentVp = contentVp;
    renderer.contentTop = contentTop;
    renderer.contentTopVp = contentTopVp;
    renderer.contentBottom = contentBottom;
    renderer.contentBottomVp = contentBottomVp;

    ////////////////////

    renderer.scrollBarWidth = scrollBarWidth;
    renderer.scrollContent = scrollContent;
    renderer.scrollVp = scrollVp;

    ////////////////////

    // const wrapper = document.createElement('div');
    const wrapper = node.renderContext.element;
    wrapper.setAttribute('wrapper', '');
    wrapper.setAttribute('grid', '');
    wrapper.style.width = renderer.opts.width == null ? '' : `${renderer.opts.width}px`;
    // wrapper.style.height = renderer.opts.height == null ? '' : `${renderer.opts.height}px`;
    wrapper.style.maxHeight = renderer.opts.height == null ? '100%' : `${renderer.opts.height}px`;
    wrapper.style.display = 'flex';
    wrapper.style.flexDirection = 'column';
    wrapper.appendChild(columnsVp);
    wrapper.appendChild(contentTopVp);
    wrapper.appendChild(contentVp);
    wrapper.appendChild(contentBottomVp);
    wrapper.appendChild(scrollVp);

    node.renderContext.innerElement = !node.renderContext.innerElement ? () => content.content : node.renderContext.innerElement;
}

function decorateDivRow(node: RowNode) {
    // const content = node.renderContext.element;
    // const wrapper = document.createElement('div');
    const wrapper = node.renderContext.element;
    wrapper.setAttribute('wrapper', '');
    wrapper.setAttribute('row', '');
    wrapper.style.whiteSpace = 'nowrap';
    // wrapper.style.height = node.def.height == null ? '' : `${node.def.height}px`;
    wrapper.style.height = `${calcRowHeight(node)}px`;
    // wrapper.appendChild(content);

    // node.renderContext.element = wrapper;
    // node.renderContext.innerElement = !node.renderContext.innerElement ? () => content : node.renderContext.innerElement;
    node.renderContext.innerElement = !node.renderContext.innerElement ? () => wrapper : node.renderContext.innerElement;
}

function decorateDivCell(node: CellNode, renderer: DivRenderer) {
    // const content = node.renderContext.element;
    // const wrapper = document.createElement('div');
    const wrapper = node.renderContext.element;
    wrapper.setAttribute('wrapper', '');
    wrapper.setAttribute('cell', '');
    wrapper.style.display = 'inline-flex';
    wrapper.style.verticalAlign = 'top';
    // wrapper.style.alignItems = 'center';
    wrapper.style.overflow = 'hidden';
    const width = calcCellWidth(node.context.c.id, renderer.columnsInternal.columns, renderer.columnsInternal.left, renderer.columnsInternal.right, node.context.c.sticky);
    wrapper.style.width = `${width}px`;
    const minWidth = node.context.c.minWidth;
    if (minWidth != null) {
        wrapper.style.minWidth = `${minWidth}px`;
    }
    wrapper.style.height = '100%';
    // wrapper.appendChild(content);

    // node.renderContext.element = wrapper;
    // node.renderContext.innerElement = !node.renderContext.innerElement ? () => content : node.renderContext.innerElement;
    node.renderContext.innerElement = !node.renderContext.innerElement ? () => wrapper : node.renderContext.innerElement;
}

export interface DivRendererOptions extends RendererOptions {
    width?: number;
    height?: number;
    horizontalScrollPadding?: number;
    verticalScrollPadding?: number;

    /**
     * resize columns to fill the grid node width, even when it resizes, without horizontal scroll.
     * when this is enabled, column resize drag handles are disabled and autoSize should not be used
     */
    autoFit?: boolean;

    /**
     * if grid node width falls below this value, use this value instead
     */
    autoFitMinWidth?: number;
}

interface LayoutEvent {
    which: 'scroll' | 'layout' | 'scroll-horizontal';
    who?: Array<string>;
}

export class DivRenderer extends Renderer {
    columnsInternal: {
        columns: Array<Column>;
        left: Array<Column>;
        right: Array<Column>;
    };
    contentColumns: {
        columns: NodeWithRows;
        left: NodeWithRows;
        right: NodeWithRows;
    };

    columns: ContentDiv;
    columnsVp: HTMLElement;
    content: ContentDiv;
    contentVp: HTMLElement;
    contentTop: ContentDiv;
    contentTopVp: HTMLElement;
    contentBottom: ContentDiv;
    contentBottomVp: HTMLElement;

    scrollBarWidth: number;
    scrollContent: HTMLElement;
    scrollVp: HTMLElement;

    scrollState: GridScrollState;
    dragState: DragState;

    rr: Renderer;

    rows: Array<RowNode>;
    rowcache: { left: Array<RowNode>; right: Array<RowNode> } = {left: [], right: []};

    done = new Subject();
    onResize = new Subject<void>();

    constructor(public readonly opts?: DivRendererOptions) {
        super(opts);
        this.rows = [];
        this.scrollState = {rowScrollStates: {}};
        this.dragState = {};
        this.rr = new Renderer();
        window['grid'] = this;

        this.columnMovementHistory = [];
    }

    flatten(): void {
        const before = this.rows.length;
        this.rows = flattenObj<GridNode, RowNode>(this.gridNode, 'children');
        const after = this.rows.length;
        const diff = before - after;
        if (diff > 0) { // TODO row addition offset
            this.scrollState.offset = diff;
        }
    }

    grid(def: Grid): GridNode {
        const node = super.grid(def);
        noZone(() => {
            merge(
                node.childrenChange.asObservable().pipe(tap(() => this.flatten())),
                node.rowsTop.childrenChange.asObservable(),
                node.rowsBottom.childrenChange.asObservable()
            ).subscribe(() => {
                // console.log('grid willlayout');
                this.layout();
            });
        });
        return node;
    }

    row(def: Row, parentNode: GridNode | RowNode): RowNode {
        const node = super.row(def, parentNode);
        noZone(() => {
            merge(
                node.childrenChange.asObservable().pipe(tap(() => this.flatten())),
                node.cells.childrenChange.asObservable()
            ).subscribe(() => {
                // console.log('row willlayout');
                this.layout();
            });
        });
        return node;
    }

    render(node: BaseNode): void {
        super.render(node);
        if (node instanceof GridNode) {
            decorateDivGrid(node, this);
            noZone(() => bindScroll(this));
        }
        if (node instanceof RowNode) {
            decorateDivRow(node);
            // node.cells.forEach(n => {
            //     this.render(n);
            //     // this.appendChild(node, n);
            //     // if (node.def.id.startsWith('column')) {
            //     //     const rows = flattenObj<NodeWithRows, RowNode>(this.contentColumns.columns, 'children');
            //     //     const i = rows.indexOf(node);
            //     //     node.renderContext.element.style.zIndex = `${rows.length - i}`;
            //     //     node.renderContext.element.style.position = 'relative';
            //     //     const l = n;
            //     //     const width = calcCellWidth(l.context.c.id, this.columnsInternal.columns, this.columnsInternal.left, this.columnsInternal.right, l.context.c.sticky, true);
            //     //     l.renderContext.element.style.width = `${width}px`;
            //     //     // l.renderContext.element.style.zIndex = `${rows.length - i}`;
            //     //     if (l.context.c.rowSpan > 1) {
            //     //         l.renderContext.element.style.height = `${sumContentHeight(rows.slice(i, l.context.c.rowSpan))}px`;
            //     //     }
            //     // }
            // });
        }
        if (node instanceof CellNode) {
            decorateDivCell(node, this);
        }
    }

    destroy(node: BaseNode): void {
        super.destroy(node);
        if (node instanceof GridNode) {
            node.forEach(n => this.destroy(n));
            this.done.next();
        }
        if (node instanceof RowNode) {
            // node.forEach(n => this.destroy(n));
            node.cells.forEach(n => {
                this.removeChild(node, n);
                this.destroy(n);
            });
        }
    }

    pending: number = 0;
    layoutt: Subject<string> = new Subject();

    appendChild(parentNode: BaseNode, node: BaseNode): void | Promise<HTMLElement> {
        if (node instanceof RowNode) return;
        if (node instanceof CellNode) return;
        const ret = super.appendChild(parentNode, node);
        if (ret instanceof Promise) {
            this.pending++;
            return ret.then(element => {
                this.pending--;
                if (this.pending === 0) {
                    // this.gridNode.childrenChange.next({which: 'append'});
                }
                return element;
            });
        }
    }

    insertBefore(parentNode: BaseNode, node: BaseNode, siblingNode: BaseNode): void | Promise<HTMLElement> {
        if (node instanceof RowNode) return;
        if (node instanceof CellNode) return;
        const ret = super.insertBefore(parentNode, node, siblingNode);
        if (ret instanceof Promise) {
            this.pending++;
            return ret.then(element => {
                this.pending--;
                if (this.pending === 0) {
                    this.gridNode.childrenChange.next({which: 'append'});
                }
                return element;
            });
        }
    }

    insertAfter(parentNode: BaseNode, node: BaseNode, siblingNode: BaseNode): void | Promise<HTMLElement> {
        if (node instanceof RowNode) return;
        if (node instanceof CellNode) return;
        const ret = super.insertAfter(parentNode, node, siblingNode);
        if (ret instanceof Promise) {
            this.pending++;
            return ret.then(element => {
                this.pending--;
                if (this.pending === 0) {
                    this.gridNode.childrenChange.next({which: 'append'});
                }
                return element;
            });
        }
    }

    removeChild(parentNode: BaseNode, node: BaseNode): void {
        if (node instanceof RowNode) {
            let n = this.rowcache.left.findIndex(e => e.def.id === node.def.id);
            if (n !== -1) {
                const nn = this.rowcache.left.splice(n, 1)[0];
                if (nn.renderContext && nn.renderContext.element.parentElement) {
                    nn.renderContext.element.parentElement.removeChild(nn.renderContext.element);
                }
            }
            n = this.rowcache.right.findIndex(e => e.def.id === node.def.id);
            if (n !== -1) {
                const nn = this.rowcache.right.splice(n, 1)[0];
                if (nn.renderContext && nn.renderContext.element.parentElement) {
                    nn.renderContext.element.parentElement.removeChild(nn.renderContext.element);
                }
            }
        }
        if (node instanceof CellNode) {
            if (node.context.c.sticky === 'left' || node.context.c.sticky === 'right') {
                if (node.renderContext && node.renderContext.element.parentElement) {
                    node.renderContext.element.parentElement.removeChild(node.renderContext.element);
                }
                return;
            }
        }
        super.removeChild(parentNode, node);
    }

    layout(who?: Array<string>): Promise<any> {
        return scheduled(from(runScroll(this, {which: 'layout', who})), animationFrameScheduler).toPromise();
    }

    // TODO support auto size tree with horizontal virtual scroll. i.e. not having to horizontalScrollPadding to a large number
    autoSizeColumnTree(): Promise<any> {
        return autoSizeColumnTree(this.contentColumns.columns.children, this);
    }

    autoSizeColumns(ids: Array<string>, skipLayout?: boolean): Promise<any> {
        // if (!ids) return;
        // let needslayout = false;
        // ids.forEach(id => {
        //     const column = findColumn(this.opts.columns, id);
        //     if (!column) return;
        //
        //     const content: Array<RowNode> = [];
        //     if (column.sticky === 'left') {
        //         content.push(...Array.from(this.columnscolumns.left.content.children).map(e => this.contentColumns.left.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.content.left.content.children).map(e => this.rowcache.left.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.contentTop.left.content.children).map(e => this.rowcache.left.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.contentBottom.left.content.children).map(e => this.rowcache.left.find(n => n.renderContext && n.renderContext.element === e)));
        //     } else if (column.sticky === 'right') {
        //         content.push(...Array.from(this.columnscolumns.right.content.children).map(e => this.contentColumns.right.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.content.right.content.children).map(e => this.rowcache.right.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.contentTop.right.content.children).map(e => this.rowcache.right.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.contentBottom.right.content.children).map(e => this.rowcache.right.find(n => n.renderContext && n.renderContext.element === e)));
        //     } else {
        //         content.push(...Array.from(this.columnscolumns.content.children).map(e => this.contentColumns.columns.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.content.content.children).map(e => this.gridNode.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.contentTop.content.children).map(e => this.gridNode.rowsTop.find(n => n.renderContext && n.renderContext.element === e)));
        //         content.push(...Array.from(this.contentBottom.content.children).map(e => this.gridNode.rowsBottom.find(n => n.renderContext && n.renderContext.element === e)));
        //     }
        //     const visibleCells: Array<CellNode> = content.reduce((p, c) => p.concat(c.cells.filter(e => e.context.c.id === id)), []);
        //     // column.width = visibleCells.reduce((p, c) => Math.max(p, getElement(c, c).clientWidth), 0);
        //     visibleCells.forEach(e => e.renderContext.element.style.width = '');
        //     const ocw = column.width;
        //     const ncw = visibleCells.reduce((p, c) => Math.max(p, c.renderContext.element.getBoundingClientRect().width), 0);
        //     // const ncw = visibleCells.reduce((p, c) => Math.max(p, c.renderContext.element.clientWidth), 0);
        //     // ncw = visibleCells.reduce((p, c) => Math.max(p, c.renderContext.element.children[0].clientWidth), 0);
        //     visibleCells.forEach(e => e.renderContext.element.style.width = `${ocw}px`);
        //     if (ocw === ncw) {
        //         return;
        //     }
        //     needslayout = true;
        //
        //     this.setColumnWidth(id, ncw);
        // });
        // if (needslayout) {
        //     return this.run({which: 'layout'}).then(() => needslayout);
        // } else {
        //     return Promise.resolve(needslayout);
        // }

        const id = ids[0];

        const column = findColumn(this.opts.columns, id);
        if (!column) return Promise.resolve(false);

        return scheduled(of(null), animationFrameScheduler)
            .pipe(
                map(() => {
                    const content: Array<RowNode> = [];
                    if (column.sticky === 'left') {
                        content.push(...Array.from(this.columns.left.content.children).map(e => this.contentColumns.left.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.content.left.content.children).map(e => this.rowcache.left.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.contentTop.left.content.children).map(e => this.rowcache.left.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.contentBottom.left.content.children).map(e => this.rowcache.left.find(n => n.renderContext && n.renderContext.element === e)));
                    } else if (column.sticky === 'right') {
                        content.push(...Array.from(this.columns.right.content.children).map(e => this.contentColumns.right.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.content.right.content.children).map(e => this.rowcache.right.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.contentTop.right.content.children).map(e => this.rowcache.right.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.contentBottom.right.content.children).map(e => this.rowcache.right.find(n => n.renderContext && n.renderContext.element === e)));
                    } else {
                        content.push(...Array.from(this.columns.content.children).map(e => this.contentColumns.columns.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.content.content.children).map(e => this.gridNode.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.contentTop.content.children).map(e => this.gridNode.rowsTop.find(n => n.renderContext && n.renderContext.element === e)));
                        content.push(...Array.from(this.contentBottom.content.children).map(e => this.gridNode.rowsBottom.find(n => n.renderContext && n.renderContext.element === e)));
                    }
                    const visibleCells: Array<CellNode> = content.reduce((p, c) => p.concat(c.cells.filter(e => e.context.c.id === id)), []);
                    // column.width = visibleCells.reduce((p, c) => Math.max(p, getElement(c, c).clientWidth), 0);
                    visibleCells.forEach(e => {
                        // e.renderContext.element.style.width = '';
                        e.def.willMeasure && e.def.willMeasure(e);
                    });
                    return visibleCells;
                }),
                // switchMap(visibleCells => scheduled(of(visibleCells), animationFrameScheduler)),
                map(visibleCells => {
                    let ocw = column.width;
                    let ncw = visibleCells.reduce((p, c, i) => {
                        let el = c.def.measureElement && c.def.measureElement(c);
                        if (!el) {
                            const valid = c.renderContext && c.renderContext.element ? Array.from(c.renderContext.element.children).filter(e => !e.hasAttribute('drag-drop')) : null;
                            el = valid && valid.length !== 0 ? valid[0] : c.renderContext && c.renderContext.element;
                        }
                        return !el ? p : Math.max(p, el.getBoundingClientRect().width)
                            + (i === visibleCells.length - 1 ? 1 : 0) /*TODO refactor. account for border being applied after*/;
                    }, 0);
                    // const ncw = visibleCells.reduce((p, c) => Math.max(p, c.renderContext.element.clientWidth), 0);
                    // ncw = visibleCells.reduce((p, c) => Math.max(p, c.renderContext.element.children[0].clientWidth), 0);
                    // console.log(aaa.ocw, aaa.ncw);
                    ncw = Math.ceil(ncw);
                    const needslayout = ocw !== ncw;
                    return {visibleCells, ocw, ncw, needslayout};
                }),
                // switchMap(aaa => scheduled(of(aaa), animationFrameScheduler)),
                tap(aaa => {
                    if (aaa.needslayout) {
                        this.setColumnWidth(id, aaa.ncw);
                    } else {
                        // aaa.visibleCells.forEach(e => e.renderContext.element.style.width = `${aaa.ocw}px`);
                    }
                    aaa.visibleCells.forEach(e => {
                        // e.renderContext.element.style.width = `${aaa.ocw}px`;
                        e.def.didMeasure && e.def.didMeasure(e);
                    });
                    if (aaa.needslayout && !skipLayout) {
                        runScroll(this, {which: 'layout'});
                    }
                }),
            ).toPromise();
    }

    moveColumns(movingIds: Array<string>, adjacentId: string, pos: 'before' | 'after'): void {
        const internalFlat = flattenArrayWithObj(this.columnsInternal.columns, 'children');
        const internalDest = internalFlat.find(c => c.children && c.children.find(cc => cc.id === adjacentId) != null).children;

        // TODO revise. temporary so that rows added after movement are in new order
        const flat = flattenArrayWithObj(this.opts.columns, 'children');
        const dest = flat.find(c => c.children && c.children.find(cc => cc.id === adjacentId) != null).children;

        [...movingIds].reverse().forEach(movingId => {
            const internalSrc = internalFlat.find(c => c.children && c.children.find(cc => cc.id === movingId) != null).children;
            const internalMovement = moveColumn(internalDest, internalSrc, movingId, adjacentId, pos);
            this.columnMovementHistory.push(internalMovement);

            const src = flat.find(c => c.children && c.children.find(cc => cc.id === movingId) != null).children;
            const movement = moveColumn(dest, src, movingId, adjacentId, pos);
            this.columnMovementHistory.push(movement);
        });
        syncCellsToColumns(this);
        runScroll(this, {which: 'layout'});
        console.log(this.columnMovementHistory)
    }

    columnMovementHistory: Array<{ dest: Array<Column>; src: Array<Column>; destIndex: number; srcIndex: number; pos: 'before' | 'after' }>;

    resetMovedColumns(): void {
        while (this.columnMovementHistory.length !== 0) {
            const movement = this.columnMovementHistory.pop();
            moveColumnByIndex(movement.src, movement.dest, movement.srcIndex, movement.destIndex);
        }
        syncCellsToColumns(this);
    }

    setColumnWidth(id: string, width: number): void {
        const column = findColumn(this.opts.columns, id);
        if (!column) return;

        const children = column.children && column.children.filter(e => !e.hidden);
        if (children && children.length !== 0) {
            let ccw = calcColumnWidth(column, true);
            if (ccw === 0) {
                ccw = 1;
            }
            children.forEach(e => {
                const ratio = calcColumnWidth(e, true) / ccw;
                this.setColumnWidth(e.id, width * ratio);
            });
            return;
        }

        column.width = width;

        const columnCells: Array<Array<BaseNode>> = [];
        this.contentColumns.columns.forEach(r => columnCells.push(r.cells.filter(l => l.context.c.id === id)));
        this.contentColumns.left.forEach(r => columnCells.push(r.cells.filter(l => l.context.c.id === id)));
        this.contentColumns.right.forEach(r => columnCells.push(r.cells.filter(l => l.context.c.id === id)));
        if (column.sticky === 'left') {
            this.rowcache.left.forEach(r => columnCells.push(r.cells.filter(l => l.context.c.id === id)));
        } else if (column.sticky === 'right') {
            this.rowcache.right.forEach(r => columnCells.push(r.cells.filter(l => l.context.c.id === id)));
        } else {
            this.gridNode.forEach(r => columnCells.push(r.cells.filter(l => l.context.c.id === id)));
            this.gridNode.rowsTop.forEach(r => columnCells.push(r.cells.filter(l => l.context.c.id === id)));
            this.gridNode.rowsBottom.forEach(r => columnCells.push(r.cells.filter(l => l.context.c.id === id)));
        }
        columnCells.reduce((p, c) => p.concat(c), [])
            .filter(l => l.renderContext)
            .forEach(l => {
                l.renderContext.element.style.width = `${width}px`;
                // l.detectChanges({onlySelf: true});
            });
    }
}

// TODO replace with a polyfill
// TODO duplicated here from insight/experimental.tabset
export class ResizeObserver {
    elSubs: Array<{ element: Element; sub: Subscription; }> = [];

    constructor(public callback) {
    }

    bind(element): Observable<Event> {
        const expand = document.createElement('div');
        expand.style.position = 'absolute';
        expand.style.left = '0px';
        expand.style.top = '0px';
        expand.style.right = '0px';
        expand.style.bottom = '0px';
        expand.style.overflow = 'hidden';
        expand.style.visibility = 'hidden';
        expand.style.zIndex = '-1';

        const expandChild = document.createElement('div');
        expandChild.style.position = 'absolute';
        expandChild.style.left = '0px';
        expandChild.style.top = '0px';
        expandChild.style.width = '10000000px';
        expandChild.style.height = '10000000px';
        expand.appendChild(expandChild);

        const shrink = document.createElement('div');
        shrink.style.position = 'absolute';
        shrink.style.left = '0px';
        shrink.style.top = '0px';
        shrink.style.right = '0px';
        shrink.style.bottom = '0px';
        shrink.style.overflow = 'hidden';
        shrink.style.visibility = 'hidden';
        expand.style.zIndex = '-1';

        const shrinkChild = document.createElement('div');
        shrinkChild.style.position = 'absolute';
        shrinkChild.style.left = '0px';
        shrinkChild.style.top = '0px';
        shrinkChild.style.width = '200%';
        shrinkChild.style.height = '200%';
        shrink.appendChild(shrinkChild);

        element.appendChild(expand);
        element.appendChild(shrink);

        const setScroll = () => {
            expand.scrollLeft = expand.scrollWidth;
            expand.scrollTop = expand.scrollHeight;

            shrink.scrollLeft = shrink.scrollWidth;
            shrink.scrollTop = shrink.scrollHeight;
        };
        setScroll();

        let rect = element.getBoundingClientRect();
        let currentWidth = rect.width;
        let currentHeight = rect.height;
        return merge(fromEvent(expand, 'scroll'),
            fromEvent(shrink, 'scroll'))
            .pipe(
                auditTime(0, animationFrameScheduler),
                tap(() => {
                    rect = element.getBoundingClientRect();

                    if (currentWidth !== rect.width || currentHeight !== rect.height) {
                        currentWidth = rect.width;
                        currentHeight = rect.height;

                        this.callback && this.callback();
                    }

                    setScroll();
                })
            );
    }

    observe(element: Element): void {
        if (this.elSubs && this.elSubs.findIndex(e => e.element === element) === -1) {
            this.elSubs.push({element, sub: this.bind(element).subscribe()});
        }
    }

    unobserve(element: Element): void {
        const index = this.elSubs.findIndex(e => e.element === element);
        if (index !== -1) {
            const elSub = this.elSubs.splice(index, 0)[0];
            elSub.sub && elSub.sub.unsubscribe();
        }
    }

    disconnect(): void {
        this.elSubs && this.elSubs.forEach(e => this.unobserve(e.element));
    }
}
